import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useMediaQuery } from "react-responsive"; // Import useMediaQuery
import { useParams, useNavigate, Link } from "react-router-dom";
import { AppContext } from "../contexts/AppContext";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
  CardDescription,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Text } from "../components/catalyst/text";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Header } from "../layout/Header";
import CustomerEngagementHistory from "../components/CustomerEngagementHistory";
import {
  ArrowLeft,
  Maximize2,
  Minimize2,
  ChevronDown,
  Clock,
  MapPin,
  Coffee,
  MessageSquare,
  CheckSquare,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  ListChecks,
  MessagesSquare,
  Info,
  BarChart3,
  Building2,
  Globe,
  Linkedin,
  Users,
  CalendarIcon,
  Newspaper,
  CheckCircle2,
  FileText,
  Edit,
  Target,
  Package,
  Heart,
  ClipboardCheck,
  AlertCircle, // Added for fallback message
  Trash2, // <-- Add Trash2 icon
  X, // <-- Add X icon
  Copy,
  Star,
  UserPlus, // <-- Add Assign Icons
  Check, // <--
  UserCheck, // <--
  UserX, // <--
  Projector, // <-- Add Projector icon for HubSpot
  RefreshCw, // <--
  UserMinus, // <--
  Loader2, // <-- Used for loading indicators
  Download, // <-- Add Download icon
  Map, // <-- Add Map icon
  Home, // <-- Add Home icon
  MessageCircle, // <-- Add MessageCircle icon
  Mail, // <-- For contact email
  Phone, // <-- For contact phone
  Search, // <-- Add Search icon
  Eye, // <-- Add Eye icon
} from "lucide-react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup as LeafletPopup,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import * as ics from "ics"; // <-- Import ics library
import { marked } from "marked";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import enUS from "date-fns/locale/en-US";
import addDays from "date-fns/addDays";
import "react-big-calendar/lib/css/react-big-calendar.css";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop"; // Import DnD HOC
import "react-big-calendar/lib/addons/dragAndDrop/styles.css"; // Import DnD styles
import { cn } from "../utils/cn";
import axios from "axios";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Separator } from "../components/ui/separator";
import { useAuth } from "../contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { toast } from "../components/ui/use-toast";

import MapSection from "../components/MapSection";
import FeedbackComponent from "../components/FeedbackComponent";
import SaveScheduleButton from "../components/SaveScheduleButton";
import useSavedSchedules from "../hooks/useSavedSchedules";
import useStopNotes from "../hooks/useStopNotes";
import { formatDate, isValidDate, safeParseISO } from "../utils/dateUtils"; // Added safeParseISO
import ChurnMetricsCard from "../components/ChurnMetricsCard";
import useChurnData from "../hooks/useChurnData";
import MessageCenter from "../components/MessageCenter";
import QORCMetricsCard from "../components/QORCMetricsCard";
import PrioritizedDataCard from "../components/PrioritizedDataCard";
import AssignRsmModal from "../components/AssignRsmModal"; // <-- Add AssignRsmModal import
import { Progress } from "../components/ui/progress"; // Import Progress
import useHubSpot from "../hooks/useHubSpot"; // Import HubSpot hook
import ContactSelector from "../components/ContactSelector"; // Import ContactSelector
import StopContacts from "../components/StopContacts"; // Import StopContacts
import contactService from "../services/contactService"; // Import ContactService
import {
  getCustomerDetails,
  getCustomerOpportunities,
} from "../services/v2ApiService"; // Import customer details and opportunities API

// --- Localizer Setup ---
const locales = {
  "en-US": enUS,
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
  formats: {
    monthHeaderFormat: "MMMM yyyy",
    dayHeaderFormat: "EEE M/d",
    weekHeaderFormat: "MMMM dd",
    dayRangeHeaderFormat: "MMMM dd",
    timeGutterFormat: "h:mm a",
    eventTimeRangeFormat: "h:mm a",
    selectRangeFormat: "h:mm a",
    agendaHeaderFormat: "MMMM dd",
    agendaDateFormat: "EEE M/d",
    agendaTimeFormat: "h:mm a",
    agendaTimeRangeFormat: "h:mm a",
  },
});

// Fix for default leaflet icon
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

// --- Calendar Formats ---
const calendarFormats = {
  monthHeaderFormat: "MMMM yyyy",
  dayHeaderFormat: "EEE M/d",
  weekHeaderFormat: "MMMM dd",
  dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, "MMMM dd", culture) +
    " - " +
    localizer.format(end, "MMMM dd", culture),
  timeGutterFormat: "h:mm a",
  eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, "h:mm a", culture) +
    " - " +
    localizer.format(end, "h:mm a", culture),
  selectRangeFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, "h:mm a", culture) +
    " - " +
    localizer.format(end, "h:mm a", culture),
  agendaHeaderFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, "MMMM dd", culture) +
    " - " +
    localizer.format(end, "MMMM dd", culture),
  agendaDateFormat: "EEE M/d",
  agendaTimeFormat: "h:mm a",
  agendaTimeRangeFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, "h:mm a", culture) +
    " - " +
    localizer.format(end, "h:mm a", culture),
};

// --- Create Drag and Drop Calendar ---
const DragAndDropCalendar = withDragAndDrop(Calendar);

// --- Helper to parse duration string (e.g., "1.5 hr") to minutes ---
const parseDurationToMinutes = (durationStr) => {
  if (!durationStr || typeof durationStr !== "string") return 60; // Default to 1 hour if invalid
  const parts = durationStr.toLowerCase().split(" ");
  let minutes = 0;
  if (parts.length > 0) {
    // Ensure parts[0] exists
    const value = parseFloat(parts[0]);
    if (!isNaN(value)) {
      if (parts.length > 1 && parts[1].startsWith("hr")) {
        // Ensure parts[1] exists for "hr"
        minutes = value * 60;
      } else if (parts.length > 1 && parts[1].startsWith("min")) {
        // Ensure parts[1] exists for "min"
        minutes = value;
      } else if (parts.length === 1 && value > 0) {
        // If only a number is provided (e.g., "30"), assume it's minutes.
        // This handles cases where units might be missing but a valid number is present.
        minutes = value;
      }
      // If value is 0 or negative, or parsing failed to set minutes, it will be handled by the return.
    }
  }
  // Ensure a positive duration, defaulting to 60 minutes.
  // If a valid positive duration was parsed, use it, but ensure it's at least, say, 15 minutes.
  return minutes > 0 ? Math.max(15, minutes) : 60;
};

// --- Helper to transform schedule data to calendar events ---

// Add this helper function within SavedByoiPage.js
function formatDurationToString(totalMinutes) {
  if (totalMinutes <= 0) return "0.25 hr"; // Default to a minimum duration like 15 mins if 0 or less
  const hours = totalMinutes / 60;
  // If hours is a whole number (e.g., 1.0, 2.0), display as "1 hr", "2 hr"
  if (Number.isInteger(hours)) {
    return `${hours} hr`;
  }
  // Otherwise, display with decimals (e.g., "0.5 hr", "1.25 hr")
  return `${parseFloat(hours.toFixed(2))} hr`;
}

const transformScheduleToEvents = (schedule) => {
  const events = [];
  if (!Array.isArray(schedule)) {
    console.warn("Schedule is not an array:", schedule);
    return events; // Ensure schedule is an array
  }

  // console.log( // Disabled for brevity
  //   "[transformScheduleToEvents] Input:",
  //   JSON.stringify(schedule, null, 2),
  // );

  // Add dayIndex to the outer loop
  schedule.forEach((daySchedule, dayIndex) => {
    // Ensure daySchedule and daySchedule.day are valid before proceeding
    if (!daySchedule || typeof daySchedule.day !== "string") {
      console.warn("Invalid day schedule format:", daySchedule);
      return; // Skip invalid day schedule
    }
    let dayDate;
    try {
      // Try parsing as YYYY-MM-DD first
      dayDate = safeParseISO(daySchedule.day + "T00:00:00"); // Ensure it's treated as local date

      if (!dayDate) {
        // Fallback parsing for "EEEE, MMMM d" format (needs current year context)
        const currentYear = new Date().getFullYear();
        const parsedFallback = parse(
          `${daySchedule.day}, ${currentYear}`,
          "EEEE, MMMM d, yyyy",
          new Date(),
        );
        if (isValidDate(parsedFallback)) {
          dayDate = parsedFallback;
        }
      }

      if (!isValidDate(dayDate)) {
        // Check if parsing failed
        console.warn(
          `Could not parse day: ${daySchedule.day}. Expected format: YYYY-MM-DD or 'EEEE, MMMM d'.`,
        );
        return; // Skip day if parsing fails
      }
    } catch (e) {
      console.error(`Error parsing day string: ${daySchedule.day}`, e);
      return; // Skip this day if error
    }

    // Ensure daySchedule.agenda is an array before iterating
    if (!Array.isArray(daySchedule.agenda)) {
      console.warn("Agenda is not an array for day:", daySchedule.day);
      return; // Skip if agenda is not an array
    }

    daySchedule.agenda.forEach((item, agendaIndex) => {
      // Ensure item and item.time are valid
      if (!item || typeof item.time !== "string") {
        console.warn("Invalid agenda item format:", item);
        return; // Skip invalid agenda item
      }
      try {
        const timeParts = item.time.split(":");
        if (timeParts.length !== 2) {
          console.warn(`Invalid time format for agenda item: ${item.time}`);
          return; // Skip item with invalid time format
        }
        const [hours, minutes] = timeParts.map(Number);
        if (isNaN(hours) || isNaN(minutes)) {
          console.warn(`Invalid hours or minutes in time: ${item.time}`);
          return; // Skip if hours/minutes are not numbers
        }

        const start = new Date(dayDate); // Use the successfully parsed dayDate
        start.setHours(hours, minutes, 0, 0);

        let durationMinutes = 60; // Default duration
        if (item.type === "break") {
          durationMinutes = 60; // Default break time
        } else if (item.estimated_onsite) {
          durationMinutes = parseDurationToMinutes(item.estimated_onsite);
        }

        const end = new Date(start.getTime() + durationMinutes * 60000);

        let eventTitle;
        let skipEvent = false;

        if (item.type === "break") {
          if (!item.label || String(item.label).trim() === "") {
            // If it's a break and has no valid label, prepare to skip it.
            console.warn(
              `[transformScheduleToEvents] Planning to skip break item due to missing or empty label. Original item data:`,
              JSON.stringify(item),
            );
            skipEvent = true;
          } else {
            eventTitle = item.label;
          }
        } else {
          // For customer stops (item.type !== "break")
          if (!item.customer_name || String(item.customer_name).trim() === "") {
            // If it's a customer stop and has no valid name, prepare to skip it.
            console.warn(
              `[transformScheduleToEvents] Planning to skip customer stop due to missing or empty customer_name. Original item data:`,
              JSON.stringify(item),
            );
            skipEvent = true;
          } else {
            eventTitle = item.customer_name;
          }
        }

        // If we decided to skip based on missing title/label, exit this iteration.
        if (skipEvent) {
          return; // This 'return' is for the forEach callback, effectively a 'continue'.
        }

        // Now, proceed with date/time validation for items that have a valid title.
        const eventIsValid =
          isValidDate(start) &&
          isValidDate(end) &&
          start.getTime() < end.getTime(); // Event must have a positive duration

        if (eventIsValid && eventTitle) {
          const event = {
            title: eventTitle || "Untitled Event", // Add fallback to prevent undefined
            start,
            end,
            allDay: false,
            resource: {
              ...item,
              originalDayIndex: dayIndex,
              originalAgendaIndex: agendaIndex,
            },
            type: item.type,
          };
          events.push(event);
        } else {
          // This warning is for items that had a title but failed date/time validation.
          console.warn(
            `[transformScheduleToEvents] Skipping event (had title: '${eventTitle}') due to invalid date/time. Start: ${start}, End: ${end}. Original item data:`,
            JSON.stringify(item),
          );
        }
      } catch (e) {
        console.error(`Error processing agenda item:`, item, e);
        // Skip this item if there's an error processing it
      }
    });
  });

  // Log the result for debugging
  // console.log( // Disabled for brevity
  //   `[transformScheduleToEvents] Successfully created ${events.length} calendar events`,
  // );

  return events;
};

// --- Custom Toolbar for React Big Calendar ---
const CustomToolbar = ({
  label,
  onView,
  onNavigate,
  views,
  view,
  isMobile,
}) => {
  const goToBack = () => onNavigate("PREV");
  const goToNext = () => onNavigate("NEXT");
  const goToCurrent = () => onNavigate("TODAY");

  // Filter views based on isMobile for the toolbar buttons
  const availableViews = isMobile ? ["day"] : views;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 items-center px-2 py-3 bg-background border-b gap-2 sm:gap-0">
      {/* Navigation Buttons */}
      <div className="flex gap-1 justify-center sm:justify-start">
        <Button
          variant="outline"
          size="sm"
          onClick={goToBack}
          className="flex items-center gap-1 px-2"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Back</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={goToCurrent}
          className="px-2"
        >
          Reset
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={goToNext}
          className="flex items-center gap-1 px-2"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      {/* Label */}
      <span className="font-semibold text-base md:text-lg text-center flex-1 whitespace-nowrap overflow-hidden text-ellipsis sm:text-center">
        {label}
      </span>
      {/* View Buttons */}
      <div className="flex gap-1 justify-center sm:justify-end">
        {availableViews.map((v) => (
          <Button
            key={v}
            type="button"
            variant={v === view ? "default" : "outline"}
            size="sm"
            className="capitalize px-2"
            onClick={() => onView(v)}
          >
            {v}
          </Button>
        ))}
      </div>
    </div>
  );
};

// Helper function to extract QORC data from customer
const extractQorcData = (customer) => {
  // Check if we have metrics data from the API
  if (customer.metrics) {
    return {
      ranking: customer.metrics.priorityranking || customer.qorc?.ranking || "",
      q_score:
        parseFloat(customer.metrics.qscore) || customer.qorc?.q_score || 0,
      o_score:
        parseFloat(customer.metrics.oscore) || customer.qorc?.o_score || 0,
      r_score:
        parseFloat(customer.metrics.rscore) || customer.qorc?.r_score || 0,
      c_score:
        parseFloat(customer.metrics.cscore) || customer.qorc?.c_score || 0,
    };
  }

  // Fallback to qorc data if available
  const qorcData = customer.qorc || {};

  // Add fallback values if needed
  return {
    ranking: qorcData.ranking || "",
    q_score: qorcData.q_score || 0,
    o_score: qorcData.o_score || 0,
    r_score: qorcData.r_score || 0,
    c_score: qorcData.c_score || 0,
    ...qorcData,
  };
};

// Custom dialog content for customer details
// Define OpportunityDialog component
const OpportunityDialog = ({
  opportunities,
  dataTypeLabel = "Opportunities",
}) => {
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showMapPopup, setShowMapPopup] = useState(false);

  // Handle sort change
  const handleSortChange = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Get the sorted opportunities
  const sortedOpportunities = [...(opportunities || [])].sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case "opportunitynum":
        aValue = a.opportunitynum || a.ordernumber || "";
        bValue = b.opportunitynum || b.ordernumber || "";
        break;
      case "date":
        aValue = a.lastupdate
          ? new Date(a.lastupdate).getTime()
          : a.createddate
            ? new Date(a.createddate).getTime()
            : 0;
        bValue = b.lastupdate
          ? new Date(b.lastupdate).getTime()
          : b.createddate
            ? new Date(b.createddate).getTime()
            : 0;
        break;
      case "amount":
        aValue = a.estimatedvalue || a.amount || 0;
        bValue = b.estimatedvalue || b.amount || 0;
        break;
      default:
        aValue = a.lastupdate ? new Date(a.lastupdate).getTime() : 0;
        bValue = b.lastupdate ? new Date(b.lastupdate).getTime() : 0;
    }

    if (sortDirection === "asc") {
      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue);
      }
      return aValue > bValue ? 1 : -1;
    } else {
      if (typeof aValue === "string" && typeof bValue === "string") {
        return bValue.localeCompare(aValue);
      }
      return aValue < bValue ? 1 : -1;
    }
  });

  // Helper to show sort indicator
  const SortIndicator = ({ field }) => {
    if (sortField !== field) return null;
    return <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>;
  };

  const titleCaseLabel =
    dataTypeLabel.charAt(0).toUpperCase() + dataTypeLabel.slice(1);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-lg">
          {titleCaseLabel} ({opportunities?.length || 0})
        </DialogTitle>
      </DialogHeader>

      <div className="flex items-center justify-between border-b pb-2 mb-2">
        <div className="text-sm text-muted-foreground">Sort by:</div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={sortField === "opportunitynum" ? "default" : "outline"}
            className="h-7 px-2 text-xs"
            onClick={() => handleSortChange("opportunitynum")}
          >
            ID/Number
            <SortIndicator field="opportunitynum" />
          </Button>
          <Button
            size="sm"
            variant={sortField === "date" ? "default" : "outline"}
            className="h-7 px-2 text-xs"
            onClick={() => handleSortChange("date")}
          >
            Date
            <SortIndicator field="date" />
          </Button>
          <Button
            size="sm"
            variant={sortField === "amount" ? "default" : "outline"}
            className="h-7 px-2 text-xs"
            onClick={() => handleSortChange("amount")}
          >
            Amount
            <SortIndicator field="amount" />
          </Button>
        </div>
      </div>

      <ScrollArea className="max-h-[60vh]">
        <div className="space-y-2 pr-4">
          {sortedOpportunities.map((opportunity, i) => (
            <div key={i} className="p-3 bg-muted/30 rounded-md border">
              <div className="flex justify-between items-start gap-2 mb-1">
                <div className="font-medium flex-1">
                  <div>
                    {opportunity.opportunityname ||
                      opportunity.opportunitynum ||
                      opportunity.ordernumber ||
                      "Unnamed"}
                  </div>
                  {(opportunity.projectcity || opportunity.projectstate) && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      <div>
                        {[opportunity.projectcity, opportunity.projectstate]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {(opportunity.estimatedvalue || opportunity.amount) && (
                    <div className="text-sm font-semibold">
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        notation: "compact",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(
                        opportunity.estimatedvalue || opportunity.amount || 0,
                      )}
                    </div>
                  )}
                  {(opportunity.projectcity || opportunity.projectstate) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={() => {
                        setSelectedOpportunity(opportunity);
                        setShowMapPopup(true);
                      }}
                      title="View on map"
                    >
                      <MapPin className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {(opportunity.projectcity || opportunity.projectstate) && (
                <div className="mt-2 p-2 bg-muted/20 rounded text-sm">
                  <div className="font-medium text-xs text-muted-foreground mb-1">
                    Project Location:
                  </div>
                  <div className="text-sm">
                    {[opportunity.projectcity, opportunity.projectstate]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-muted-foreground mt-2">
                {opportunity.markettype && (
                  <div className="col-span-2 mb-0.5">
                    <span className="inline-block bg-muted px-1.5 py-0.5 rounded-sm">
                      {opportunity.markettype}
                    </span>
                    {opportunity.api_status && (
                      <span className="inline-block ml-1 bg-muted/70 px-1.5 py-0.5 rounded-sm">
                        {opportunity.api_status}
                      </span>
                    )}
                  </div>
                )}

                {opportunity.lastupdate &&
                  isValidDate(opportunity.lastupdate) && (
                    <>
                      <span>Updated:</span>
                      <span>
                        {formatDate(opportunity.lastupdate, "MM/dd/yyyy")}
                      </span>
                    </>
                  )}

                {opportunity.createddate &&
                  isValidDate(opportunity.createddate) && (
                    <>
                      <span>Created:</span>
                      <span>
                        {formatDate(opportunity.createddate, "MM/dd/yyyy")}
                      </span>
                    </>
                  )}

                {(opportunity.opportunitynum || opportunity.ordernumber) && (
                  <>
                    <span>ID/Num:</span>
                    <span>
                      {opportunity.opportunitynum || opportunity.ordernumber}
                    </span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </>
  );
};

const CustomerDialogContent = ({
  customer,
  isLoading,
  customerDetailsData,
  customerOpportunitiesData,
  setSelectedCustomerOpportunities,
  setOpportunitiesDialogOpen,
  getChurnDataForCustomer,
  churnLoading,
  handleOpenProgressDialog,
  handleOpenHubspotDialog,
}) => {
  if (!customer) return null;

  // Get customer details from enhanced data or fallback to basic customer data
  const customerCode = customer.customer_id || customer.id;
  const customerDetails = customerDetailsData[customerCode] || customer;
  const customerOpportunities = customerOpportunitiesData[customerCode];

  // Show loading state when fetching additional details
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="animate-spin mb-4">
          <Clock className="h-8 w-8 text-primary" />
        </div>
        <p className="text-sm text-muted-foreground">
          Loading additional customer details...
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="max-h-[70vh]">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 md:p-6">
        {/* Left Column */}
        <div className="space-y-3 sm:space-y-4">
          {/* Combined Customer & Company Info Card - matching ItineraryDetailsPage */}
          <Card className="bg-background shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                Customer & Company Info
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Customer Basic Info */}
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Customer Details
                  </h4>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name:</span>
                      <span className="font-medium">
                        {customer.customer_name ||
                          customer.name ||
                          "Unknown Customer"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ID:</span>
                      <span className="font-medium">
                        {customer.customer_id || customer.customercode || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Address:</span>
                      <span className="font-medium text-right flex-1 ml-2">
                        {customer.address ||
                          customer.formatted_address ||
                          "No address provided"}
                      </span>
                    </div>
                    {(customer.city || customer.state || customer.zipcode) && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Location:</span>
                        <span className="font-medium">
                          {[customer.city, customer.state, customer.zipcode]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Customer Notes */}
                {customer.notes && (
                  <div className="space-y-2 pt-2 border-t">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Notes
                    </h4>
                    <p className="text-sm text-foreground bg-muted/30 rounded p-2">
                      {customer.notes}
                    </p>
                  </div>
                )}

                {/* Company Profile Info */}
                {customerDetails?.company_profile && (
                  <div className="space-y-2 pt-2 border-t">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Company Profile
                    </h4>
                    <div className="space-y-1.5 text-sm">
                      {customerDetails.company_profile.industry && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Industry:
                          </span>
                          <Badge variant="secondary" className="text-xs">
                            {customerDetails.company_profile.industry}
                          </Badge>
                        </div>
                      )}
                      {customerDetails.company_profile.website_url && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Website:
                          </span>
                          <a
                            href={customerDetails.company_profile.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline flex items-center gap-1"
                          >
                            <Globe className="w-3 h-3" />
                            Visit
                          </a>
                        </div>
                      )}
                      {customerDetails.company_profile.company_summary && (
                        <div className="space-y-1">
                          <span className="text-muted-foreground text-xs">
                            Summary:
                          </span>
                          <p className="text-xs text-foreground leading-relaxed bg-muted/30 rounded p-2">
                            {customerDetails.company_profile.company_summary}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Action Items Card */}
          <Card className="bg-background shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                <ListChecks className="w-4 h-4" />
                Action Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              {customerDetails.action_items &&
              customerDetails.action_items.length > 0 ? (
                <ul className="space-y-2">
                  {customerDetails.action_items.map((item, i) => (
                    <li key={i} className="flex gap-2 items-start">
                      <CheckSquare className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No action items available.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Discussion Points Card */}
          <Card className="bg-background shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                <MessagesSquare className="w-4 h-4" />
                Discussion Points
              </CardTitle>
            </CardHeader>
            <CardContent>
              {customerDetails.discussion_points &&
              customerDetails.discussion_points.length > 0 ? (
                <ul className="space-y-2">
                  {customerDetails.discussion_points.map((point, i) => (
                    <li key={i} className="flex gap-2 items-start">
                      <MessageSquare className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No discussion points available.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Location Card with Maps Button */}
          <Card className="bg-background shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                Location
              </CardTitle>
              {(customer.location || customer.formatted_address) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    const address = encodeURIComponent(
                      customer.location || customer.formatted_address,
                    );
                    window.open(
                      `https://www.google.com/maps/search/?api=1&query=${address}`,
                      "_blank",
                    );
                  }}
                >
                  <Map className="h-4 w-4" />
                  <span className="hidden sm:inline">Map</span>
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="text-sm">
                {customer.formatted_address ||
                  customer.location ||
                  "No address provided"}
              </div>
            </CardContent>
          </Card>

          {/* Churn Metrics Card */}
          <ChurnMetricsCard
            churnData={getChurnDataForCustomer(
              String(customer.customer_id || customer.customercode || ""),
            )}
            isLoading={churnLoading}
            customerCode={String(
              customer.customer_id || customer.customercode || "",
            )}
          />

          {/* Engagement History Card */}
          <CustomerEngagementHistory customer={customer} />

          {/* Site Notes Card */}
          <Card className="bg-background shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                <Info className="w-4 h-4" />
                Site Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground italic">
                No site notes available.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-3 sm:space-y-4">
          {/* Customer Scorecard - enhanced */}
          {customerDetails.qscore ||
          customerDetails.oscore ||
          customerDetails.rscore ||
          customerDetails.cscore ? (
            <Card className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/50 dark:to-gray-900/50 shadow-md border-slate-200 dark:border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <div className="p-1.5 bg-primary/10 rounded-lg">
                    <BarChart3 className="w-4 h-4 text-primary" />
                  </div>
                  Customer Scorecard
                </CardTitle>
                {customerDetails.prioritybucket && (
                  <Badge
                    variant={
                      customerDetails.prioritybucket === "High"
                        ? "default"
                        : customerDetails.prioritybucket === "Medium"
                          ? "secondary"
                          : "outline"
                    }
                  >
                    {customerDetails.prioritybucket} Priority
                  </Badge>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-muted/30 border rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-foreground">
                      {customerDetails.qscore || "N/A"}
                    </p>
                    <p className="text-xs text-muted-foreground font-medium">
                      Q-Score
                    </p>
                  </div>
                  <div className="bg-muted/30 border rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-foreground">
                      {customerDetails.oscore || "N/A"}
                    </p>
                    <p className="text-xs text-muted-foreground font-medium">
                      O-Score
                    </p>
                  </div>
                  <div className="bg-muted/30 border rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-foreground">
                      {customerDetails.rscore || "N/A"}
                    </p>
                    <p className="text-xs text-muted-foreground font-medium">
                      R-Score
                    </p>
                  </div>
                  <div className="bg-muted/30 border rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-foreground">
                      {customerDetails.cscore || "N/A"}
                    </p>
                    <p className="text-xs text-muted-foreground font-medium">
                      C-Score
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <QORCMetricsCard
              qorc={extractQorcData(customerDetails)}
              primaryFocus={customerDetails.primary_focus}
            />
          )}

          {/* Prioritized Data Card */}
          <PrioritizedDataCard customer={customerDetails} />

          {/* Business Metrics Card */}
          {customerDetails.order_count ||
          customerDetails.order_value ||
          customerDetails.quote_count ||
          customerDetails.quote_value ? (
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 shadow-md border-green-200 dark:border-green-700">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 dark:bg-green-900 rounded-lg">
                    <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  Business Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-muted/30 border rounded-lg p-3">
                    <p className="text-lg font-bold text-foreground">
                      {customerDetails.order_count || 0}
                    </p>
                    <p className="text-xs text-muted-foreground font-medium">
                      Total Orders
                    </p>
                    <p className="text-sm font-semibold text-foreground mt-1">
                      ${(customerDetails.order_value || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-muted/30 border rounded-lg p-3">
                    <p className="text-lg font-bold text-foreground">
                      {customerDetails.quote_count || 0}
                    </p>
                    <p className="text-xs text-muted-foreground font-medium">
                      Total Quotes
                    </p>
                    <p className="text-sm font-semibold text-foreground mt-1">
                      ${(customerDetails.quote_value || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {/* Active Opportunities Card */}
          {(customerDetails.open_quotes ||
            customerDetails.open_orders ||
            customerDetails.active_rentals) && (
            <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/30 dark:to-blue-900/30 shadow-md border-indigo-200 dark:border-indigo-700">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                    <Target className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  Active Quotes, Orders and Rentals
                </CardTitle>
                {/* View All Button */}
                {((customerDetails.open_quotes &&
                  customerDetails.open_quotes.length > 0) ||
                  (customerDetails.open_orders &&
                    customerDetails.open_orders.length > 0) ||
                  (customerDetails.active_rentals &&
                    customerDetails.active_rentals.length > 0)) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-6"
                    onClick={() => {
                      // Format opportunities for the dialog
                      const allOpportunities =
                        customerOpportunities?.data || [];
                      const quotes =
                        allOpportunities.filter(
                          (opp) => opp.type === "quote",
                        ) ||
                        customerDetails.open_quotes ||
                        [];
                      const orders =
                        allOpportunities.filter(
                          (opp) => opp.type === "order",
                        ) ||
                        customerDetails.open_orders ||
                        [];
                      const rentals =
                        allOpportunities.filter(
                          (opp) => opp.type === "rental",
                        ) ||
                        customerDetails.active_rentals ||
                        [];

                      setSelectedCustomerOpportunities({
                        customer: customer,
                        details: {
                          ...customerDetails,
                          open_quotes: quotes,
                          open_orders: orders,
                          active_rentals: rentals,
                        },
                        opportunities: allOpportunities,
                      });
                      setOpportunitiesDialogOpen(true);
                    }}
                  >
                    View All
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                      {(customerDetails.open_quotes &&
                        customerDetails.open_quotes.length) ||
                        0}
                    </div>
                    <div className="text-xs text-muted-foreground font-medium">
                      Quotes
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                      {(customerDetails.open_orders &&
                        customerDetails.open_orders.length) ||
                        0}
                    </div>
                    <div className="text-xs text-muted-foreground font-medium">
                      Orders
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                      {(customerDetails.active_rentals &&
                        customerDetails.active_rentals.length) ||
                        0}
                    </div>
                    <div className="text-xs text-muted-foreground font-medium">
                      Rentals
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Account Metrics Card */}
          {customerDetails.metrics &&
            Object.keys(customerDetails.metrics).length > 0 && (
              <Card className="bg-background shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                    <BarChart3 className="w-4 h-4" />
                    Account Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 text-center">
                    {Object.entries(customerDetails.metrics)
                      .filter(
                        ([key]) =>
                          ![
                            "qscore",
                            "oscore",
                            "rscore",
                            "cscore",
                            "priorityranking",
                          ].includes(key.toLowerCase()),
                      )
                      .map(([key, value]) => {
                        const labels = {
                          cltv: "CLTV",
                          order_freq: "Order Freq.",
                          quote_freq: "Quote Freq.",
                          open_quotes: "Open Qts",
                          aov: "AOV",
                          aqv: "AQV",
                        };
                        const isCurrency = ["cltv", "aov", "aqv"].includes(key);
                        const displayValue = isCurrency
                          ? new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "USD",
                              notation: "compact",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(value || 0)
                          : value;

                        return (
                          <div
                            key={key}
                            className="bg-muted/50 rounded-md p-2 border"
                          >
                            <div className="text-xs text-muted-foreground uppercase tracking-wide">
                              {labels[key] || key.replace(/_/g, " ")}
                            </div>
                            <div className="text-lg font-semibold text-foreground mt-1">
                              {displayValue ?? "N/A"}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}

          {/* Site Notes Card */}
          <Card className="bg-background shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                <Info className="w-4 h-4" />
                Site Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {customer.site_notes ? (
                <div className="text-sm whitespace-pre-wrap">
                  {customer.site_notes}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No site notes available.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </ScrollArea>
  );
};

// Small map popup component for showing opportunity location
const OpportunityMapPopup = ({ opportunity, isOpen, onClose }) => {
  // Build address string
  const addressParts = [
    opportunity.address,
    opportunity.city,
    opportunity.state,
    opportunity.zipcode,
  ].filter(Boolean);

  const fullAddress = addressParts.join(", ");

  // Use coordinates if available, otherwise default to US center
  const defaultCenter = [39.8283, -98.5795];
  const [mapCenter, setMapCenter] = useState(
    opportunity.coordinates &&
      opportunity.coordinates.lat &&
      opportunity.coordinates.lng
      ? [opportunity.coordinates.lat, opportunity.coordinates.lng]
      : defaultCenter,
  );

  useEffect(() => {
    // Update map center if coordinates are available
    if (
      opportunity.coordinates &&
      opportunity.coordinates.lat &&
      opportunity.coordinates.lng
    ) {
      setMapCenter([opportunity.coordinates.lat, opportunity.coordinates.lng]);
    }
  }, [opportunity.coordinates]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {opportunity.opportunityname ||
              opportunity.opportunitynum ||
              opportunity.ordernumber ||
              "Location"}
          </DialogTitle>
          <DialogDescription>
            {fullAddress || "No address available"}
          </DialogDescription>
        </DialogHeader>
        <div style={{ height: "400px", width: "100%" }}>
          <MapContainer
            center={mapCenter}
            zoom={opportunity.coordinates ? 15 : 4}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {fullAddress && (
              <Marker position={mapCenter}>
                <LeafletPopup>
                  <div>
                    <strong>
                      {opportunity.opportunityname || "Opportunity"}
                    </strong>
                    <br />
                    {fullAddress}
                  </div>
                </LeafletPopup>
              </Marker>
            )}
          </MapContainer>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function SavedByoiPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  // Additional hooks for customer popup functionality
  const { loading: churnLoading, getChurnDataForCustomer } = useChurnData();
  
  // HubSpot integration hook
  const {
    contacts: hubspotContacts,
    loadingContacts: hubspotLoading,
    error: hubspotError,
    hasMoreContacts,
    fetchAllContacts,
    loadMoreContacts,
    searchContact,
    createContact,
    findOrCreateContact,
    createEngagement,
    getCustomerEngagements,
    associateEngagementWithCustomer,
  } = useHubSpot();

  // State from ItineraryDetailsPage
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clusterData, setClusterData] = useState(null);
  const [modifiedScheduleData, setModifiedScheduleData] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarInitialized, setCalendarInitialized] = useState(false);
  const [isMapFullScreen, setIsMapFullScreen] = useState(false);
  const [activeTab, setActiveTab] = useState("schedule");
  const [showReasoningDialog, setShowReasoningDialog] = useState(false);

  // Feedback states
  const [directionsFeedback, setDirectionsFeedback] = useState("");
  const [itineraryFeedback, setItineraryFeedback] = useState("");

  const [reasoningFeedback, setReasoningFeedback] = useState("");
  const [feedbackContext, setFeedbackContext] = useState("");

  // BYOI specific states
  const [itineraries, setItineraries] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedItinerary, setSelectedItinerary] = useState(null);

  // Notes functionality states
  const [bmNotesOpen, setBmNotesOpen] = useState(false);
  const [rsmNotesOpen, setRsmNotesOpen] = useState(false);
  const [bmNotes, setBmNotes] = useState("");
  const [rsmNotes, setRsmNotes] = useState("");
  const [currentCustomerForNotes, setCurrentCustomerForNotes] = useState(null);
  // Customer opportunities states
  const [selectedCustomerOpportunities, setSelectedCustomerOpportunities] =
    useState(null);
  const [opportunitiesDialogOpen, setOpportunitiesDialogOpen] = useState(false);
  const [opportunitiesFilter, setOpportunitiesFilter] = useState("all");
  const [opportunitiesSort, setOpportunitiesSort] = useState("amount_desc");
  const [opportunitiesSearch, setOpportunitiesSearch] = useState("");
  const [showOnlyMappable, setShowOnlyMappable] = useState(false);
  const [customerOpportunities, setCustomerOpportunities] = useState(null);
  // Enhanced customer data states (matching ItineraryDetailsPage)
  const [customerDetailsData, setCustomerDetailsData] = useState({});
  const [customerOpportunitiesData, setCustomerOpportunitiesData] = useState(
    {},
  );
  
  // Contact management states
  const [customerContactCounts, setCustomerContactCounts] = useState({});
  const [contactSelectorOpen, setContactSelectorOpen] = useState(false);
  const [contactsViewOpen, setContactsViewOpen] = useState(false);
  const [selectedStopForContacts, setSelectedStopForContacts] = useState(null);
  const [customerDetailsLoading, setCustomerDetailsLoading] = useState({});
  const [customerOpportunitiesLoading, setCustomerOpportunitiesLoading] =
    useState({});

  // Map popup states for opportunities
  const [mapPopupOpen, setMapPopupOpen] = useState(false);
  const [selectedMapOpportunity, setSelectedMapOpportunity] = useState(null);

  // RSM assignment states
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [unassignLoading, setUnassignLoading] = useState(false);

  // User role checks
  const isBM =
    user?.role?.toLowerCase() === "bm" || user?.role?.toLowerCase() === "admin";
  const isRSM =
    user?.role?.toLowerCase() === "rsm" ||
    user?.role?.toLowerCase() === "admin";

  // Debug logging
  console.log("[SavedByoiPage] User role check:", {
    userRole: user?.role,
    isBM,
    isRSM,
    selectedItinerary: selectedItinerary?.itinerary_id,
    assigned_rsm_id: selectedItinerary?.assigned_rsm_id,
  });

  const prevClusterIdRef = useRef(null);

  // Memoized calendar events based on schedule data
  const modifiedCalendarEvents = useMemo(() => {
    return transformScheduleToEvents(modifiedScheduleData);
  }, [modifiedScheduleData]);

  // Customers derived from schedule data
  const customers = useMemo(() => {
    if (!Array.isArray(modifiedScheduleData)) return [];

    const customerList = [];
    modifiedScheduleData.forEach((daySchedule) => {
      if (Array.isArray(daySchedule.agenda)) {
        daySchedule.agenda.forEach((item) => {
          if (item.type !== "break" && item.customer_name) {
            // Ensure location data is available for the map
            const fullAddress = [
              item.address,
              item.city,
              item.state,
              item.zipcode,
            ]
              .filter(Boolean)
              .join(", ");
            const location = item.location || fullAddress;

            // Use coordinates if available, otherwise fall back to address
            let finalLocation = location;
            if (
              item.coordinates &&
              item.coordinates.lat &&
              item.coordinates.lng
            ) {
              finalLocation = `${item.coordinates.lat},${item.coordinates.lng}`;
            }

            // Debug logging for location data
            console.log("Customer location data:", {
              customerName: item.customer_name,
              originalLocation: item.location,
              coordinates: item.coordinates,
              fullAddress: fullAddress,
              finalLocation: finalLocation,
              addressParts: {
                address: item.address,
                city: item.city,
                state: item.state,
                zipcode: item.zipcode,
              },
            });

            customerList.push({
              id: item.customer_id || item.id || customerList.length,
              name: item.customer_name,
              customername: item.customer_name, // Add this for MapSection compatibility
              customer_id: item.customer_id,
              address: item.address || "",
              city: item.city || "",
              state: item.state || "",
              zipcode: item.zipcode || "",
              location: finalLocation, // Use finalLocation which prioritizes coordinates
              coordinates: item.coordinates || null, // Pass coordinates to customer object
              formatted_address: fullAddress, // Add this for MapSection compatibility
              primary_focus: item.primary_focus,
              prioritybucket: item.prioritybucket || "medium", // Add default priority for MapSection
              status: item.status || "PENDING",
              _uniqueId: `${item.customer_id || item.id || customerList.length}_${Date.now()}`, // Add unique ID for MapSection
            });
          }
        });
      }
    });

    console.log(
      "Final customers array for map:",
      customerList.length,
      customerList,
    );
    return customerList;
  }, [modifiedScheduleData]);

  // Directions request for map
  const directionsRequest = useMemo(() => {
    if (!customers || customers.length === 0) return null;

    // Filter customers with valid location data
    const customersWithLocation = customers.filter((customer) => {
      const location = customer.location || customer.formatted_address;
      return location && location.trim() && location !== "N/A";
    });

    if (customersWithLocation.length < 2) return null;

    const origin = customersWithLocation[0];
    const destination = customersWithLocation[customersWithLocation.length - 1];
    const waypoints = customersWithLocation.slice(1, -1);

    console.log("Directions request data:", {
      origin: origin.location || origin.formatted_address,
      destination: destination.location || destination.formatted_address,
      waypoints: waypoints.map((wp) => wp.location || wp.formatted_address),
      totalCustomers: customers.length,
      customersWithLocation: customersWithLocation.length,
    });

    return {
      origin: { location: origin.location || origin.formatted_address },
      destination: {
        location: destination.location || destination.formatted_address,
      },
      waypoints: waypoints.map((customer) => ({
        location: customer.location || customer.formatted_address,
        stopover: true,
      })),
      travelMode: "DRIVING",
      optimizeWaypoints: false,
    };
  }, [customers]);

  useEffect(() => {
    fetchItineraries();
  }, [user]);

  const fetchItineraries = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const response = await axios.get("/api/itineraries/byoi");

      if (response.data.success) {
        setItineraries(response.data.data.itineraries || []);
      }
    } catch (error) {
      console.error("Error fetching itineraries:", error);
      toast({
        title: "Failed to load itineraries",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (itineraryId) => {
    try {
      const response = await axios.delete(
        `/api/itineraries/byoi/${itineraryId}`,
      );

      if (response.data.success) {
        toast({
          title: "Itinerary deleted",
          description: "The itinerary has been removed successfully",
        });
        fetchItineraries();
      }
    } catch (error) {
      console.error("Error deleting itinerary:", error);
      toast({
        title: "Failed to delete itinerary",
        description: "Please try again later",
        variant: "destructive",
      });
    }
    setDeleteConfirm(null);
  };

  const handleViewDetails = async (itineraryId) => {
    try {
      const response = await axios.get(`/api/itineraries/byoi/${itineraryId}`);
      if (response.data.success) {
        const itinerary = response.data.data;

        // Transform the itinerary data to match ItineraryDetailsPage format
        const transformedSchedule = transformByoiScheduleToItineraryFormat(
          itinerary.schedule,
        );

        setSelectedItinerary(itinerary);
        setClusterData({
          cluster: itinerary.itinerary_id,
          metadata: {
            itinerary_name: itinerary.name,
            brainstorm: itinerary.description,
          },
          priority: "Custom",
          itinerary: {
            customers: itinerary.schedule || [],
            schedule: transformedSchedule,
          },
          assigned_to: {
            id: itinerary.assigned_rsm_id,
            full_name: itinerary.assigned_rsm_name,
          },
        });
        setModifiedScheduleData(transformedSchedule);
        setHasUnsavedChanges(false);
        setCalendarInitialized(false);
        setFeedbackContext(`byoi_${itinerary.itinerary_id}`);
      }
    } catch (error) {
      console.error("Error fetching itinerary details:", error);
      toast({
        title: "Failed to load details",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  };

  // Transform BYOI schedule format to ItineraryDetailsPage format
  const transformByoiScheduleToItineraryFormat = (byoiSchedule) => {
    if (!Array.isArray(byoiSchedule)) return [];

    const groupedByDay = {};

    byoiSchedule.forEach((stop, index) => {
      const startTime = new Date(stop.start_time);
      const dayKey = format(startTime, "yyyy-MM-dd");

      if (!groupedByDay[dayKey]) {
        groupedByDay[dayKey] = {
          day: dayKey,
          agenda: [],
        };
      }

      // Ensure location data is properly formatted for the map
      const fullAddress = [stop.address, stop.city, stop.state, stop.zipcode]
        .filter(Boolean)
        .join(", ");

      groupedByDay[dayKey].agenda.push({
        time: format(startTime, "HH:mm"),
        customer_name: stop.customer_name,
        customer_id: stop.customer_id,
        address: stop.address || "",
        city: stop.city || "",
        state: stop.state || "",
        zipcode: stop.zipcode || "",
        location: stop.location || fullAddress, // Use provided location or construct from address parts
        coordinates: stop.coordinates || null, // Add coordinates from backend
        primary_focus:
          stop.customer_details?.primary_focus || stop.primary_focus,
        estimated_onsite: "1 hr",
        type: "customer",
        status: stop.status || "PENDING",
        id: stop.customer_id || index, // Ensure each stop has an ID
      });
    });

    return Object.values(groupedByDay);
  };

  const filteredItineraries = itineraries.filter(
    (itinerary) =>
      itinerary.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      itinerary.description.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getPriorityBadgeVariant = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "destructive";
      case "medium":
        return "secondary";
      case "low":
        return "outline";
      default:
        return "outline";
    }
  };

  const toggleFullScreenMap = () => {
    setIsMapFullScreen(!isMapFullScreen);
    setTimeout(() => window.dispatchEvent(new Event("resize")), 150);
  };

  // Data preparation
  const reasoning = clusterData?.metadata?.brainstorm || null;
  const itineraryName =
    clusterData?.metadata?.itinerary_name ||
    (clusterData ? `Schedule ${clusterData.cluster}` : "Loading Schedule...");
  const itineraryPriority = clusterData?.priority || "N/A";

  // Reset navigation when cluster data changes or events change
  useEffect(() => {
    if (
      clusterData &&
      modifiedCalendarEvents.length > 0 &&
      !calendarInitialized
    ) {
      const firstEventDate = modifiedCalendarEvents[0].start;
      if (isValidDate(firstEventDate)) {
        setCurrentDate(firstEventDate);
      } else {
        setCurrentDate(new Date());
      }
      setCalendarInitialized(true);
    } else if (!clusterData || modifiedCalendarEvents.length === 0) {
      setCurrentDate(new Date());
      setCalendarInitialized(false);
    }
  }, [clusterData, modifiedCalendarEvents, calendarInitialized]);

  // Handle navigate
  const handleNavigate = (newDate, view, action) => {
    let targetDate = newDate;

    if (!isValidDate(targetDate)) {
      console.warn(
        `[handleNavigate] Received invalid date: ${targetDate}. Falling back to current date.`,
      );
      targetDate = new Date();
    }

    if (action === "TODAY") {
      if (modifiedCalendarEvents && modifiedCalendarEvents.length > 0) {
        const firstEventDate = modifiedCalendarEvents[0].start;
        if (isValidDate(firstEventDate)) {
          targetDate = firstEventDate;
        } else {
          targetDate = new Date();
        }
      } else {
        targetDate = new Date();
      }
    }

    if (!isValidDate(targetDate)) {
      console.error(
        "[handleNavigate] After processing, date is still invalid. Using current date as fallback.",
      );
      targetDate = new Date();
    }

    setCurrentDate(targetDate);
  };

  // Event style getter for calendar
  const eventStyleGetter = (event, start, end, isSelected) => {
    let backgroundColor = "hsl(var(--primary) / 0.85)";
    let color = "hsl(var(--primary-foreground))";
    let borderColor = "hsl(var(--primary))";
    let opacity = isSelected ? 1 : 0.9;

    if (event.type === "break") {
      backgroundColor = "hsl(38 92% 50% / 0.85)";
      color = "hsl(38 95% 10%)";
      borderColor = "hsl(38 92% 50%)";
    } else if (event.type === "travel") {
      backgroundColor = "hsl(var(--muted) / 0.7)";
      color = "hsl(var(--muted-foreground))";
      borderColor = "hsl(var(--border))";
      opacity = isSelected ? 0.9 : 0.7;
    }

    const style = {
      backgroundColor,
      color,
      borderRadius: "calc(var(--radius) - 2px)",
      border: `1px solid ${borderColor}`,
      opacity: opacity,
      display: "flex",
      flexDirection: "column",
      padding: "3px 6px",
      fontSize: "0.7rem",
      lineHeight: "1.3",
      boxShadow: isSelected
        ? "0 0 0 1px hsl(var(--background)), 0 0 0 3px hsl(var(--primary))"
        : "none",
      transition: "opacity 0.2s ease-in-out, background-color 0.2s ease-in-out",
      overflow: "hidden",
      cursor: event.type !== "break" ? "grab" : "default",
    };

    return { style };
  };

  // Custom event component
  const CustomEvent = ({ event }) => {
    if (!event || !event.title) {
      console.error("[CustomEvent] Invalid event object:", event);
      return null;
    }

    return (
      <div className="relative h-full flex flex-col justify-center">
        <div className="px-1">
          <div className="font-medium truncate text-[11px]">
            {event.title || "Untitled"}
          </div>
          <div className="text-[10px] opacity-80">
            {event.start && format(event.start, "h:mm a")} -{" "}
            {event.end && format(event.end, "h:mm a")}
          </div>
        </div>
      </div>
    );
  };

  // Time settings for calendar
  const timeSettings = useMemo(() => {
    const minTime = new Date();
    minTime.setHours(8, 0, 0);
    const maxTime = new Date();
    maxTime.setHours(18, 0, 0);
    const scrollToTime = new Date();
    scrollToTime.setHours(8, 0, 0);
    return { minTime, maxTime, scrollToTime };
  }, []);

  const scrollToTimeValue = timeSettings.scrollToTime;

  // Export to ICS
  const handleExportICS = () => {
    if (!modifiedCalendarEvents || modifiedCalendarEvents.length === 0) return;

    const events = modifiedCalendarEvents.map((event) => {
      const start = event.start;
      const end = event.end;

      return {
        title: event.title,
        description: `Visit to ${event.title}`,
        location: event.resource?.address || "",
        start: [
          start.getFullYear(),
          start.getMonth() + 1,
          start.getDate(),
          start.getHours(),
          start.getMinutes(),
        ],
        end: [
          end.getFullYear(),
          end.getMonth() + 1,
          end.getDate(),
          end.getHours(),
          end.getMinutes(),
        ],
        categories: ["work", "customer-visit"],
        status: "CONFIRMED",
        busyStatus: "BUSY",
      };
    });

    ics.createEvents(events, (error, value) => {
      if (error) {
        console.error(error);
        toast({
          title: "Export failed",
          description: "Failed to export calendar file",
          variant: "destructive",
        });
        return;
      }

      const blob = new Blob([value], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${itineraryName.replace(/\s+/g, "_")}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    });
  };

  // Handle opening BM notes dialog
  const handleOpenBmNotesDialog = (customer) => {
    setCurrentCustomerForNotes(customer);
    setBmNotes(
      customer
        ? customer.bm_notes?.notes || ""
        : clusterData?.bm_notes?.notes || "",
    );
    setBmNotesOpen(true);
  };

  // Handle opening RSM notes dialog
  const handleOpenRsmNotesDialog = () => {
    setRsmNotes(clusterData?.rsm_notes?.notes || "");
    setRsmNotesOpen(true);
  };

  // Handle updating BM notes
  const handleUpdateBmNotes = async () => {
    if (!selectedItinerary) return;

    try {
      const payload = {
        notes: bmNotes,
        type: currentCustomerForNotes ? "customer" : "schedule",
        customer_id: currentCustomerForNotes?.customer_id,
      };

      const response = await axios.post(
        `/api/notes/byoi/${selectedItinerary.itinerary_id}/bm`,
        payload,
      );

      if (response.data.success) {
        toast({
          title: "Notes saved",
          description: "Business Manager notes have been updated successfully",
        });

        // Update local state
        if (currentCustomerForNotes) {
          // Update customer notes in modifiedScheduleData
          const updatedSchedule = modifiedScheduleData.map((day) => ({
            ...day,
            agenda: day.agenda.map((item) =>
              item.customer_id === currentCustomerForNotes.customer_id
                ? {
                    ...item,
                    bm_notes: {
                      notes: bmNotes,
                      updated_at: new Date().toISOString(),
                    },
                  }
                : item,
            ),
          }));
          setModifiedScheduleData(updatedSchedule);
        } else {
          // Update schedule-level notes
          setClusterData((prev) => ({
            ...prev,
            bm_notes: { notes: bmNotes, updated_at: new Date().toISOString() },
          }));
        }

        setBmNotesOpen(false);
        setBmNotes("");
        setCurrentCustomerForNotes(null);
      }
    } catch (error) {
      console.error("Error updating BM notes:", error);
      toast({
        title: "Failed to save notes",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  };

  // Handle updating RSM notes
  const handleUpdateRsmNotes = async () => {
    if (!selectedItinerary) return;

    try {
      const payload = {
        notes: rsmNotes,
        type: "schedule",
      };

      const response = await axios.post(
        `/api/notes/byoi/${selectedItinerary.itinerary_id}/rsm`,
        payload,
      );

      if (response.data.success) {
        toast({
          title: "Notes saved",
          description: "RSM notes have been updated successfully",
        });

        // Update local state
        setClusterData((prev) => ({
          ...prev,
          rsm_notes: { notes: rsmNotes, updated_at: new Date().toISOString() },
        }));

        setRsmNotesOpen(false);
        setRsmNotes("");
      }
    } catch (error) {
      console.error("Error updating RSM notes:", error);
      toast({
        title: "Failed to save notes",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  };

  // customerDetailsCache already declared above


  // Fetch customer details for enhanced dialog (matching ItineraryDetailsPage)
  const fetchCustomerDetailsForDialog = useCallback(
    async (customerCode) => {
      if (
        !customerCode ||
        customerDetailsData[customerCode] ||
        customerDetailsLoading[customerCode]
      ) {
        return; // Already have data or loading
      }

      setCustomerDetailsLoading((prev) => ({ ...prev, [customerCode]: true }));

      try {
        const response = await getCustomerDetails(customerCode, token);
        setCustomerDetailsData((prev) => ({
          ...prev,
          [customerCode]: response,
        }));
      } catch (error) {
        console.error("Error fetching customer details:", error);
        toast({
          title: "Failed to load customer details",
          description: "Please try again later",
          variant: "destructive",
        });
      } finally {
        setCustomerDetailsLoading((prev) => ({
          ...prev,
          [customerCode]: false,
        }));
      }
    },
    [customerDetailsData, customerDetailsLoading, token],
  );

  // Fetch customer opportunities for enhanced dialog
  const fetchCustomerOpportunitiesForDialog = useCallback(
    async (customerCode) => {
      if (
        !customerCode ||
        customerOpportunitiesData[customerCode] ||
        customerOpportunitiesLoading[customerCode]
      ) {
        return; // Already have data or loading
      }

      setCustomerOpportunitiesLoading((prev) => ({
        ...prev,
        [customerCode]: true,
      }));

      try {
        const response = await getCustomerOpportunities(customerCode, token);
        setCustomerOpportunitiesData((prev) => ({
          ...prev,
          [customerCode]: response,
        }));
      } catch (error) {
        console.error("Error fetching customer opportunities:", error);
        // Don't show toast for opportunities - they're not critical for display
      } finally {
        setCustomerOpportunitiesLoading((prev) => ({
          ...prev,
          [customerCode]: false,
        }));
      }
    },
    [customerOpportunitiesData, customerOpportunitiesLoading, token],
  );

  // Handler functions for RSM/BM actions in customer popup
  const handleOpenProgressDialog = (customer) => {
    // Placeholder function - in SavedByoiPage this feature is not implemented
    toast({
      title: "Feature not available",
      description: "Status updates are not available for saved itineraries",
      variant: "default"
    });
  };

  const handleOpenHubspotDialog = (customer) => {
    // Placeholder function - in SavedByoiPage this feature is not implemented
    toast({
      title: "Feature not available", 
      description: "HubSpot updates are not available for saved itineraries",
      variant: "default"
    });
  };

  // Handle unassign RSM
  const handleUnassignRsm = async () => {
    if (!selectedItinerary) return;

    setUnassignLoading(true);
    try {
      const response = await axios.post(
        `/api/itineraries/byoi/${selectedItinerary.itinerary_id}/unassign`,
      );

      if (response.data.success) {
        toast({
          title: "RSM Unassigned",
          description: "The RSM has been removed from this itinerary",
        });

        // Update local state
        setSelectedItinerary((prev) => ({
          ...prev,
          assigned_rsm_id: null,
          assigned_rsm_name: null,
        }));

        // Refresh itineraries list
        fetchItineraries();
      }
    } catch (error) {
      console.error("Error unassigning RSM:", error);
      toast({
        title: "Failed to unassign RSM",
        description: error.response?.data?.error || "Please try again later",
        variant: "destructive",
      });
    } finally {
      setUnassignLoading(false);
    }
  };

  // Handle assign RSM complete
  const handleAssignComplete = () => {
    setIsAssignModalOpen(false);

    // Refresh the current itinerary and list
    if (selectedItinerary) {
      handleViewDetails(selectedItinerary.itinerary_id);
    }
    fetchItineraries();
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!selectedItinerary || !hasUnsavedChanges) return;

    try {
      // Transform modified schedule back to BYOI format
      const updatedSchedule = [];
      modifiedScheduleData.forEach((daySchedule) => {
        if (Array.isArray(daySchedule.agenda)) {
          daySchedule.agenda.forEach((item, index) => {
            if (item.type !== "break") {
              const [hours, minutes] = item.time.split(":").map(Number);
              const startDate = new Date(daySchedule.day + "T00:00:00");
              startDate.setHours(hours, minutes, 0, 0);

              const endDate = new Date(startDate);
              endDate.setHours(startDate.getHours() + 1); // Default 1 hour duration

              updatedSchedule.push({
                customer_id: item.customer_id,
                customer_name: item.customer_name,
                start_time: startDate.toISOString(),
                end_time: endDate.toISOString(),
                address: item.address,
                city: item.city,
                state: item.state,
                zipcode: item.zipcode,
                location: item.location,
                coordinates: item.coordinates || null, // Add coordinates if available
                status: item.status || "PENDING",
                order: updatedSchedule.length + 1,
              });
            }
          });
        }
      });

      const payload = {
        ...selectedItinerary,
        schedule: updatedSchedule,
      };

      const response = await axios.put(
        `/api/itineraries/byoi/${selectedItinerary.itinerary_id}`,
        payload,
      );

      if (response.data.success) {
        toast({
          title: "Changes saved",
          description: "Your itinerary has been updated successfully",
        });
        setHasUnsavedChanges(false);
        fetchItineraries();
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      toast({
        title: "Failed to save changes",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  };

  // Contact management functions
  const handleOpenContactSelector = (stop) => {
    setSelectedStopForContacts(stop);
    setContactSelectorOpen(true);
  };

  const handleViewContacts = (stop) => {
    setSelectedStopForContacts(stop);
    setContactsViewOpen(true);
  };

  const handleDeleteStop = async (customerId) => {
    try {
      if (!selectedItinerary) {
        toast({
          title: "Error",
          description: "No itinerary selected",
          variant: "destructive",
        });
        return;
      }

      console.log('Deleting stop for customer:', customerId, 'from itinerary:', selectedItinerary.itinerary_id);

      // Use the dedicated delete stop endpoint
      const response = await axios.delete(
        `/api/itineraries/byoi/${selectedItinerary.itinerary_id}/stops/${customerId}`
      );

      if (response.data.success) {
        toast({
          title: "Stop deleted",
          description: `Stop has been removed from the itinerary. ${response.data.remaining_stops} stops remaining.`,
        });
        
        // Refresh the itinerary data
        await handleViewDetails(selectedItinerary.itinerary_id);
      } else {
        throw new Error(response.data.message || "Failed to delete stop");
      }
    } catch (error) {
      console.error("Error deleting stop:", error);
      
      // Handle specific error cases
      if (error.response?.status === 404) {
        toast({
          title: "Stop not found",
          description: "The stop may have already been removed or does not exist",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Failed to delete stop",
          description: error.response?.data?.detail || error.message || "Please try again later",
          variant: "destructive",
        });
      }
    }
  };

  // Mobile bottom navigation
  const MobileBottomNav = () => {
    return (
      <div className="sticky bottom-0 z-10 bg-background border-t">
        <TabsList className="grid grid-cols-4 w-full rounded-none h-16 bg-background">
          <TabsTrigger
            value="schedule"
            className="flex flex-col items-center justify-center space-y-1 h-full rounded-none data-[state=active]:bg-transparent data-[state=active]:border-t-2 data-[state=active]:border-primary data-[state=active]:text-primary"
          >
            <div className="flex flex-col items-center">
              <Home className="h-5 w-5 mb-1" />
              <span className="text-xs font-medium">Schedule</span>
            </div>
          </TabsTrigger>
          <TabsTrigger
            value="stops"
            className="flex flex-col items-center justify-center space-y-1 h-full rounded-none data-[state=active]:bg-transparent data-[state=active]:border-t-2 data-[state=active]:border-primary data-[state=active]:text-primary"
          >
            <div className="flex flex-col items-center">
              <Users className="h-5 w-5 mb-1" />
              <span className="text-xs font-medium">
                Stops ({customers.length})
              </span>
            </div>
          </TabsTrigger>
          <TabsTrigger
            value="notes"
            className="flex flex-col items-center justify-center space-y-1 h-full rounded-none data-[state=active]:bg-transparent data-[state=active]:border-t-2 data-[state=active]:border-primary data-[state=active]:text-primary"
          >
            <div className="flex flex-col items-center">
              <FileText className="h-5 w-5 mb-1" />
              <span className="text-xs font-medium">Notes</span>
            </div>
          </TabsTrigger>
          <TabsTrigger
            value="messages"
            className="flex flex-col items-center justify-center space-y-1 h-full rounded-none data-[state=active]:bg-transparent data-[state=active]:border-t-2 data-[state=active]:border-primary data-[state=active]:text-primary"
          >
            <div className="flex flex-col items-center">
              <MessageCircle className="h-5 w-5 mb-1" />
              <span className="text-xs font-medium">Messages</span>
            </div>
          </TabsTrigger>
        </TabsList>
      </div>
    );
  };

  // If viewing a specific itinerary, show detailed view exactly like ItineraryDetailsPage
  if (selectedItinerary) {
    return (
      <div
        className={`flex flex-col ${isMapFullScreen ? "fixed inset-0 bg-background z-50" : "relative p-4 md:p-6 lg:p-8"}`}
      >
        {/* Header and Top Bar (conditional rendering) */}
        {!isMapFullScreen && (
          <div className="flex-shrink-0">
            <Header />
            <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedItinerary(null)}
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <div className="flex-1 min-w-0 flex flex-col">
                <div className="w-full flex items-center">
                  <h2 className="text-xl md:text-2xl font-semibold truncate mr-2">
                    {itineraryName}
                  </h2>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Badge
                      variant={getPriorityBadgeVariant(itineraryPriority)}
                      className="capitalize whitespace-nowrap"
                    >
                      {itineraryPriority}
                    </Badge>
                    {reasoning && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setShowReasoningDialog(true)}
                        title="View generation reasoning"
                      >
                        <Info className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {/* RSM Assignment Display */}
                <div className="text-sm text-muted-foreground mt-1">
                  {selectedItinerary?.assigned_rsm_id ? (
                    <div className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4" />
                      <span>
                        Assigned to:{" "}
                        {selectedItinerary.assigned_rsm_name || "RSM"}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-600">
                      <AlertCircle className="h-4 w-4" />
                      <span>Not assigned to any RSM</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* RSM Assignment Button - only show for BM */}
                {isBM &&
                  (selectedItinerary?.assigned_rsm_id ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUnassignRsm}
                      disabled={unassignLoading}
                    >
                      {unassignLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Unassigning...
                        </>
                      ) : (
                        <>
                          <UserMinus className="h-4 w-4 mr-1" />
                          Unassign RSM
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => {
                        console.log(
                          "[SavedByoiPage] Assign RSM button clicked",
                        );
                        setIsAssignModalOpen(true);
                      }}
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      Assign RSM
                    </Button>
                  ))}
                <Button
                  onClick={toggleFullScreenMap}
                  variant="ghost"
                  size="icon"
                  className="flex-shrink-0"
                >
                  <Maximize2 className="h-5 w-5" />
                  <span className="sr-only">Fullscreen Map</span>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Map Container */}
        <Card
          className={`overflow-hidden flex flex-col ${isMapFullScreen ? "flex-grow border-0 rounded-none shadow-none" : "h-[300px] md:h-[350px] mb-6 shadow-md flex-shrink-0"}`}
        >
          {isMapFullScreen && (
            <CardHeader className="p-2 flex-shrink-0 flex justify-end absolute top-2 right-2 z-10 bg-background/80 rounded">
              <Button onClick={toggleFullScreenMap} variant="ghost" size="icon">
                <Minimize2 className="h-5 w-5" />
                <span className="sr-only">Exit Fullscreen</span>
              </Button>
            </CardHeader>
          )}
          <CardContent
            className={`p-0 flex-grow ${isMapFullScreen ? "" : "h-full"}`}
          >
            <MapSection
              customers={customers}
              directionsRequest={directionsRequest}
              handleDirectionsCallback={(response) => {
                // console.log("Directions response on details page:", response);
              }}
            />
          </CardContent>
        </Card>

        {/* Tabs and Content Area (Only if not fullscreen) */}
        {!isMapFullScreen && (
          <div className={`${isMobile ? "pb-16" : ""}`}>
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              {/* Desktop TabsList - hidden on mobile */}
              <TabsList
                className={cn(
                  "grid w-full grid-cols-2 sm:grid-cols-4 mb-4 flex-shrink-0 sticky top-0 bg-background z-10 p-1 rounded-md",
                  isMobile && "hidden",
                )}
              >
                <TabsTrigger
                  value="schedule"
                  className="transition-all text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow"
                >
                  Schedule
                </TabsTrigger>
                <TabsTrigger
                  value="stops"
                  className="transition-all text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow"
                >
                  Stops ({customers.length})
                </TabsTrigger>
                <TabsTrigger
                  value="notes"
                  className="transition-all text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow"
                >
                  Notes
                </TabsTrigger>
                <TabsTrigger
                  value="messages"
                  className="transition-all text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow"
                >
                  Messages
                </TabsTrigger>
              </TabsList>

              {/* Tabs Content - Make it scrollable on mobile */}
              <div
                className={`${isMobile ? "h-[calc(100vh-480px)] overflow-y-auto pb-16" : ""}`}
              >
                {/* Tab Content (Schedule) */}
                <TabsContent value="schedule" className="mt-0">
                  {/* Save/Export Buttons Container */}
                  <div className="flex flex-col sm:flex-row justify-end gap-2 mb-2">
                    {hasUnsavedChanges && (
                      <Button
                        size="sm"
                        className="w-full sm:w-auto"
                        onClick={handleSaveChanges}
                        disabled={loading}
                      >
                        {loading ? "Saving..." : "Save Changes"}
                      </Button>
                    )}
                    {/* Export ICS Button */}
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleExportICS}
                      disabled={
                        !modifiedCalendarEvents ||
                        modifiedCalendarEvents.length === 0
                      }
                      title="Export schedule as .ics file"
                      className="w-full sm:w-auto"
                      style={{
                        backgroundColor: "#0078D4",
                        borderColor: "#0078D4",
                        color: "#FFFFFF",
                      }}
                    >
                      <CalendarIcon className="mr-1.5 h-4 w-4" />
                      Export ICS
                    </Button>
                  </div>

                  <Card>
                    <CardContent className="p-0 relative">
                      {/* Calendar explicit height - now responsive */}
                      <div className="h-[60vh] sm:h-[600px]">
                        {isValidDate(currentDate) &&
                        Array.isArray(modifiedCalendarEvents) ? (
                          <DragAndDropCalendar
                            localizer={localizer}
                            formats={calendarFormats}
                            events={
                              modifiedCalendarEvents.length > 0
                                ? modifiedCalendarEvents
                                : []
                            }
                            startAccessor="start"
                            endAccessor="end"
                            defaultView={isMobile ? "day" : "week"}
                            views={isMobile ? ["day"] : ["week", "day"]}
                            step={30}
                            timeslots={2}
                            showMultiDayTimes
                            eventPropGetter={eventStyleGetter}
                            min={timeSettings.minTime}
                            max={timeSettings.maxTime}
                            scrollToTime={scrollToTimeValue}
                            onNavigate={handleNavigate}
                            date={currentDate}
                            components={{
                              toolbar: (toolbarProps) => (
                                <CustomToolbar
                                  {...toolbarProps}
                                  isMobile={isMobile}
                                />
                              ),
                              event: CustomEvent,
                            }}
                            style={{ height: "100%" }}
                            selectable
                          />
                        ) : (
                          <div className="h-full flex items-center justify-center bg-muted/30 rounded-md border">
                            <div className="text-center p-4">
                              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
                              <p className="text-muted-foreground">
                                Loading calendar...
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  <div className={`${isMobile ? "" : "mt-6"}`}>
                    {/* Directions Feedback */}
                    <div className={`${isMobile ? "mt-4 px-1" : "mt-6"}`}>
                      <Card className="shadow-sm">
                        <CardContent
                          className={`p-3 ${isMobile ? "pt-3 pb-2" : "p-4"}`}
                        >
                          <FeedbackComponent
                            question="How accurate are the driving directions/times?"
                            feedback={directionsFeedback}
                            setFeedback={setDirectionsFeedback}
                            feedbackKey="directions"
                            context={feedbackContext}
                            showRatings={true}
                            ratingLabels={[
                              "Accuracy",
                              "Efficiency",
                              "Clarity",
                              "Overall",
                            ]}
                            isMobile={isMobile}
                          />
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab Content (Stops) */}
                <TabsContent value="stops" className="space-y-4 mt-0">
                  <Card>
                    <CardContent className="p-4">
                      {customers.length > 0 ? (
                        <div className="rounded-md border">
                          <div className="divide-y divide-border">
                            {customers.map((customer, idx) => (
                              <Dialog key={customer.id || idx}>
                                <DialogTrigger asChild>
                                  <button
                                    type="button"
                                    className="w-full text-left p-3 sm:p-4 border-b border-muted hover:bg-muted/50 cursor-pointer transition-colors flex items-center justify-between"
                                    onClick={() => {
                                      const customerCode =
                                        customer.customer_id || customer.id;
                                      if (customerCode) {
                                        fetchCustomerDetailsForDialog(
                                          customerCode,
                                        );
                                        fetchCustomerOpportunitiesForDialog(
                                          customerCode,
                                        );
                                      }
                                    }}
                                  >
                                    {/* Left side: Index + Customer Name + Contact Status + Opportunities */}
                                    <div className="flex flex-1 items-center gap-2 sm:gap-3 min-w-0">
                                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                                        {idx + 1}
                                      </div>
                                      <div className="flex flex-col min-w-0">
                                        <div className="font-medium text-sm uppercase truncate">
                                          {customer.name || "Unnamed Customer"}
                                        </div>
                                        
                                        {/* Contact Status Indicator */}
                                        <div className="flex items-center gap-1 mt-1">
                                          {(() => {
                                            const customerId = customer.customer_id || customer.id;
                                            const contactCount = customerContactCounts[customerId] || 0;
                                            return contactCount > 0 ? (
                                              <Badge variant="secondary" className="text-xs px-2 py-0.5">
                                                <Users className="h-3 w-3 mr-1" />
                                                {contactCount} contact{contactCount !== 1 ? 's' : ''}
                                              </Badge>
                                            ) : (
                                              <Badge variant="outline" className="text-xs px-2 py-0.5 text-muted-foreground border-muted-foreground/30">
                                                <Users className="h-3 w-3 mr-1" />
                                                No contacts
                                              </Badge>
                                            );
                                          })()}
                                        </div>
                                        
                                        {/* View Opportunities Button */}
                                        {customer.enhanced_opportunities && customer.enhanced_opportunities.length > 0 && (
                                          <Dialog>
                                            <DialogTrigger asChild>
                                              <button
                                                type="button"
                                                className="flex items-center gap-1 mt-1 text-xs text-primary hover:text-primary/80 transition-colors group"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  e.preventDefault();
                                                }}
                                              >
                                                <Target className="h-3 w-3" />
                                                <span>View Opportunities</span>
                                                <Badge variant="secondary" className="ml-1 text-xs">
                                                  {customer.enhanced_opportunities.length}
                                                </Badge>
                                                <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                              </button>
                                            </DialogTrigger>
                                            <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0">
                                              <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
                                                <DialogTitle className="flex items-center gap-2">
                                                  <Target className="h-5 w-5" />
                                                  Address Specific Opportunities
                                                  <Badge variant="secondary" className="ml-2">
                                                    {customer.enhanced_opportunities.length}
                                                  </Badge>
                                                </DialogTitle>
                                                <DialogDescription>
                                                  Opportunities for {customer.name || "Unknown Customer"}
                                                </DialogDescription>
                                              </DialogHeader>
                                              <div className="flex-1 overflow-y-auto px-6 py-4">
                                                <div className="space-y-3 pb-4">
                                                  {customer.enhanced_opportunities.map((opp, oppIdx) => (
                                                    <div key={oppIdx} className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/30 dark:to-blue-900/30 border-l-4 border-indigo-500 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200">
                                                      <div className="flex items-start justify-between mb-3">
                                                        <div className="flex items-center gap-3">
                                                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 shadow-sm">
                                                            <FileText className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                                                          </div>
                                                          <div>
                                                            <Badge variant="outline" className="text-xs font-medium text-indigo-600 dark:text-indigo-400 border-current mb-1">
                                                              {opp.opportunity_type || 'Opportunity'}
                                                            </Badge>
                                                            <span className="text-sm font-semibold text-foreground block">
                                                              {opp.value_id || opp.opportunityname || 'Opportunity'}
                                                            </span>
                                                          </div>
                                                        </div>
                                                        {opp.estimatedvalue && (
                                                          <div className="text-right">
                                                            <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                                                              ${Number(opp.estimatedvalue).toLocaleString()}
                                                            </div>
                                                          </div>
                                                        )}
                                                      </div>
                                                      
                                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                                        {opp.city && opp.state && (
                                                          <div className="flex items-center gap-2">
                                                            <MapPin className="h-4 w-4 text-muted-foreground" />
                                                            <span className="text-muted-foreground">Location:</span>
                                                            <span className="font-medium">{opp.city}, {opp.state}</span>
                                                          </div>
                                                        )}
                                                        {opp.opportunitynum && (
                                                          <div className="flex items-center gap-2">
                                                            <FileText className="h-4 w-4 text-muted-foreground" />
                                                            <span className="text-muted-foreground">ID:</span>
                                                            <span className="font-mono font-medium bg-muted px-2 py-1 rounded text-xs">
                                                              {opp.opportunitynum}
                                                            </span>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            </DialogContent>
                                          </Dialog>
                                        )}
                                        
                                        {customer.primary_focus && (
                                          <div className="flex items-center gap-1 mt-0.5">
                                            <Target className="h-3 w-3 text-muted-foreground" />
                                            <span className="text-xs text-muted-foreground truncate">
                                              {customer.primary_focus.split(":")[0]}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Right side with action buttons */}
                                    <div className="flex flex-shrink-0 items-center space-x-1">
                                      {/* Maps button */}
                                      <button
                                        type="button"
                                        className="flex items-center justify-center h-7 w-7 p-1 rounded-full bg-muted/30 hover:bg-primary/10 hover:text-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          const addressParts = [
                                            customer.address,
                                            customer.city,
                                            customer.state,
                                            customer.zipcode,
                                          ].filter(Boolean);
                                          const fullAddress =
                                            addressParts.join(", ");
                                          if (fullAddress) {
                                            const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
                                            window.open(
                                              googleMapsUrl,
                                              "_blank",
                                              "noopener,noreferrer",
                                            );
                                          } else {
                                            toast({
                                              title: "Location Data Missing",
                                              description:
                                                "Cannot open in Google Maps, address is incomplete.",
                                              variant: "destructive",
                                            });
                                          }
                                        }}
                                        aria-label="Open in Google Maps"
                                        title="Open in Google Maps"
                                      >
                                        <Map className="h-3.5 w-3.5" />
                                      </button>
                                      
                                      {/* Contact management buttons */}
                                      {(isRSM || isBM) && (
                                        <button
                                          type="button"
                                          className="flex items-center justify-center h-7 w-7 p-1 rounded-full bg-muted/30 hover:bg-primary/10 hover:text-primary"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleOpenContactSelector(customer);
                                          }}
                                          aria-label="Manage contacts"
                                          title="Manage contacts"
                                        >
                                          <UserPlus className="h-3.5 w-3.5" />
                                        </button>
                                      )}
                                      
                                      <button
                                        type="button"
                                        className="flex items-center justify-center h-7 w-7 p-1 rounded-full bg-muted/30 hover:bg-primary/10 hover:text-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          handleViewContacts(customer);
                                        }}
                                        aria-label="View contacts"
                                        title="View contacts"
                                      >
                                        <Users className="h-3.5 w-3.5" />
                                      </button>
                                      
                                      {/* Delete button for BM */}
                                      {isBM && (
                                        <button
                                          type="button"
                                          className="flex items-center justify-center h-7 w-7 p-1 rounded-full bg-muted/30 hover:bg-destructive/10 hover:text-destructive"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            
                                            if (!customer.customer_id && !customer.id && idx === undefined) {
                                              console.error('❌ No identifier found for customer:', customer);
                                              alert('Error: Cannot delete stop - no identifier found. Please refresh the page and try again.');
                                              return;
                                            }
                                            
                                            if (window.confirm("Are you sure you want to permanently remove this stop? This cannot be undone.")) {
                                              // Pass the customer_id or use the index as identifier
                                              handleDeleteStop(customer.customer_id || customer.id || idx);
                                            }
                                          }}
                                          aria-label="Remove stop"
                                          title="Remove stop"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      )}
                                      
                                      {/* Chevron indicator */}
                                      <div className="flex items-center justify-center h-7 w-7 p-1">
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                      </div>
                                    </div>
                                  </button>
                                </DialogTrigger>
                                <DialogContent className="w-full max-w-[320px] sm:max-w-lg md:max-w-3xl lg:max-w-4xl xl:max-w-5xl p-0">
                                  <DialogHeader className="p-3 sm:p-4 border-b bg-muted/30">
                                    <div className="flex items-center justify-between w-full">
                                      <div className="flex items-center gap-2 sm:gap-3">
                                        <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-base sm:text-lg font-bold">
                                           {idx + 1}
                                        </div>
                                      </div>
                                      {(isRSM || isBM) && (
                                        <Dialog>
                                          <DialogTrigger asChild>
                                            <Button 
                                              variant="outline" 
                                              size="sm" 
                                              className="gap-1.5 mr-16"
                                              onClick={() => {
                                                // Fetch HubSpot contacts when dialog opens for both RSM and BM
                                                fetchAllContacts();
                                              }}
                                            >
                                              <Users className="h-4 w-4" />
                                              Manage Contacts
                                            </Button>
                                          </DialogTrigger>
                                        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
                                          <DialogHeader className="pb-4">
                                            <DialogTitle className="text-lg">Manage Contacts</DialogTitle>
                                            <DialogDescription className="text-sm">
                                              View and manage all contacts in HubSpot. Add new contacts for customer engagements.
                                            </DialogDescription>
                                          </DialogHeader>
                                          <div className="grid gap-6 overflow-y-auto flex-1 pr-2">
                                            {/* Existing Contacts Section */}
                                            <div className="space-y-3">
                                              <div className="flex items-center justify-between">
                                                <h3 className="text-sm font-semibold flex items-center gap-2">
                                                  <Users className="h-4 w-4 text-muted-foreground" />
                                                  Existing Contacts
                                                  {hubspotContacts && hubspotContacts.length > 0 && (
                                                    <span className="text-xs text-muted-foreground font-normal">
                                                      ({hubspotContacts.length} total)
                                                    </span>
                                                  )}
                                                </h3>
                                              </div>
                                              
                                              <div className="border rounded-lg bg-muted/10 p-1">
                                                {hubspotLoading ? (
                                                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-8">
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    Loading contacts...
                                                  </div>
                                                ) : hubspotContacts && hubspotContacts.length > 0 ? (
                                                  <ScrollArea className="h-[200px] pr-3">
                                                    <div className="space-y-2 p-2">
                                                      {hubspotContacts.map((contact) => (
                                                        <div key={contact.id} className="p-3 border rounded-lg bg-background hover:bg-muted/50 transition-colors">
                                                          <div className="flex items-start justify-between gap-3">
                                                            <div className="flex-1 min-w-0">
                                                              <div className="font-medium text-sm">
                                                                {contact.properties.firstname} {contact.properties.lastname}
                                                              </div>
                                                              <div className="text-xs text-muted-foreground space-y-1 mt-1">
                                                                {contact.properties.email && (
                                                                  <div className="flex items-center gap-1.5">
                                                                    <Mail className="h-3 w-3 flex-shrink-0" />
                                                                    <span className="truncate">{contact.properties.email}</span>
                                                                  </div>
                                                                )}
                                                                {contact.properties.phone && (
                                                                  <div className="flex items-center gap-1.5">
                                                                    <Phone className="h-3 w-3 flex-shrink-0" />
                                                                    <span>{contact.properties.phone}</span>
                                                                  </div>
                                                                )}
                                                              </div>
                                                            </div>
                                                            <div className="text-xs text-muted-foreground">
                                                              #{contact.id}
                                                            </div>
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  </ScrollArea>
                                                ) : hubspotError ? (
                                                  <div className="flex items-center justify-center gap-2 text-sm text-destructive py-8">
                                                    <AlertCircle className="h-4 w-4" />
                                                    Error: {hubspotError}
                                                  </div>
                                                ) : (
                                                  <div className="flex items-center justify-center text-sm text-muted-foreground py-8">
                                                    No contacts found in HubSpot
                                                  </div>
                                                )}
                                              </div>
                                              
                                              {hasMoreContacts && hubspotContacts && hubspotContacts.length > 0 && (
                                                <Button 
                                                  variant="outline" 
                                                  size="sm" 
                                                  onClick={loadMoreContacts}
                                                  disabled={hubspotLoading}
                                                  className="w-full mt-2"
                                                >
                                                  {hubspotLoading ? (
                                                    <>
                                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                      Loading more...
                                                    </>
                                                  ) : (
                                                    `Load More Contacts`
                                                  )}
                                                </Button>
                                              )}
                                            </div>
                                            
                                            {/* Add New Contact Form */}
                                            <div className="space-y-3">
                                              <Separator />
                                              <h3 className="text-sm font-semibold flex items-center gap-2">
                                                <UserPlus className="h-4 w-4 text-muted-foreground" />
                                                Add New Contact
                                              </h3>
                                              <div className="grid gap-4 p-4 border rounded-lg bg-muted/10">
                                                <div className="grid grid-cols-2 gap-3">
                                                  <div className="space-y-2">
                                                    <label htmlFor="firstName" className="text-sm font-medium">
                                                      First Name <span className="text-destructive">*</span>
                                                    </label>
                                                    <Input 
                                                      id="firstName" 
                                                      placeholder="John" 
                                                      className="h-9"
                                                    />
                                                  </div>
                                                  <div className="space-y-2">
                                                    <label htmlFor="lastName" className="text-sm font-medium">
                                                      Last Name <span className="text-destructive">*</span>
                                                    </label>
                                                    <Input 
                                                      id="lastName" 
                                                      placeholder="Doe" 
                                                      className="h-9"
                                                    />
                                                  </div>
                                                </div>
                                                <div className="space-y-2">
                                                  <label htmlFor="email" className="text-sm font-medium">
                                                    Email <span className="text-destructive">*</span>
                                                  </label>
                                                  <Input 
                                                    id="email" 
                                                    placeholder="john.doe@example.com" 
                                                    type="email" 
                                                    className="h-9"
                                                  />
                                                </div>
                                                <div className="space-y-2">
                                                  <label htmlFor="phone" className="text-sm font-medium">
                                                    Phone Number
                                                  </label>
                                                  <Input 
                                                    id="phone" 
                                                    placeholder="+1 (555) 123-4567" 
                                                    className="h-9"
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          <DialogFooter className="flex-col sm:flex-row gap-2 pt-4 flex-shrink-0 border-t">
                                            <Button 
                                              type="button" 
                                              variant="outline"
                                              size="sm"
                                              className="w-full sm:w-auto"
                                              onClick={() => {
                                                // Clear the form when canceling
                                                const firstNameInput = document.getElementById('firstName');
                                                const lastNameInput = document.getElementById('lastName');
                                                const emailInput = document.getElementById('email');
                                                const phoneInput = document.getElementById('phone');
                                                
                                                if (firstNameInput) firstNameInput.value = '';
                                                if (lastNameInput) lastNameInput.value = '';
                                                if (emailInput) emailInput.value = '';
                                                if (phoneInput) phoneInput.value = '';
                                              }}
                                            >
                                              Clear Form
                                            </Button>
                                            <Button 
                                              type="button"
                                              size="sm"
                                              className="w-full sm:w-auto"
                                              onClick={async () => {
                                                // Validate required fields
                                                const firstNameInput = document.getElementById('firstName');
                                                const lastNameInput = document.getElementById('lastName');
                                                const emailInput = document.getElementById('email');
                                                const phoneInput = document.getElementById('phone');
                                                
                                                if (!firstNameInput?.value || !lastNameInput?.value || !emailInput?.value) {
                                                  toast({
                                                    title: "Missing Information",
                                                    description: "Please fill in all required fields (First Name, Last Name, Email)",
                                                    variant: "destructive",
                                                  });
                                                  return;
                                                }
                                                
                                                // Validate email format
                                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                                if (!emailRegex.test(emailInput.value)) {
                                                  toast({
                                                    title: "Invalid Email",
                                                    description: "Please enter a valid email address",
                                                    variant: "destructive",
                                                  });
                                                  return;
                                                }
                                                
                                                // Create contact in HubSpot (for both RSM and BM)
                                                const newContact = {
                                                  first_name: firstNameInput.value,
                                                  last_name: lastNameInput.value,
                                                  email: emailInput.value,
                                                  phone: phoneInput?.value || ""
                                                };
                                                
                                                // Call the HubSpot API
                                                try {
                                                  const response = await createContact(newContact);
                                                  
                                                  if (response?.status === "success") {
                                                    toast({
                                                      title: "Success",
                                                      description: "Contact added successfully",
                                                    });
                                                    
                                                    // Clear the form
                                                    if (firstNameInput) firstNameInput.value = '';
                                                    if (lastNameInput) lastNameInput.value = '';
                                                    if (emailInput) emailInput.value = '';
                                                    if (phoneInput) phoneInput.value = '';
                                                    
                                                    // The createContact function already refreshes the contacts list
                                                  } else {
                                                    throw new Error("Failed to create contact in HubSpot");
                                                  }
                                                } catch (error) {
                                                  console.error("Error adding HubSpot contact:", error);
                                                  toast({
                                                    title: "Error",
                                                    description: error.message || "Failed to add contact to HubSpot",
                                                    variant: "destructive",
                                                  });
                                                }
                                              }}
                                            >
                                              <UserPlus className="h-4 w-4 mr-1" />
                                              Add Contact
                                            </Button>
                                          </DialogFooter>
                                        </DialogContent>
                                      </Dialog>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 sm:gap-3">
                                         <div>
                                           <DialogTitle className="text-xl font-semibold uppercase">
                                             {customer.name || "Unnamed Customer"}
                                           </DialogTitle>
                                           <DialogDescription className="flex items-center gap-2 text-xs text-muted-foreground">
                                             <span>ID: {customer.customer_id || customer.id || "N/A"}</span>
                                             {customer.priority?.tier && (
                                       <Badge
                                         variant="secondary"
                                                 size="sm"
                                                 className="capitalize"
                                       >
                                                 {customer.priority.tier} Tier
                                       </Badge>
                                     )}
                                         </DialogDescription>
                                       </div>
                                     </div>
                                   </DialogHeader>
                                   {/* Check if customer details are loading */}
                                   {(() => {
                                     const customerCode = customer.id;
                                     const isLoading = customerDetailsLoading[customerCode];
                                     const customerDetails = customerDetailsData[customerCode];

                                     if (isLoading) {
                                       return (
                                         <div className="flex items-center justify-center py-12">
                                           <div className="text-center space-y-4">
                                             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                                             <div className="space-y-2">
                                               <p className="text-sm font-medium text-foreground">Loading customer details...</p>
                                               <p className="text-xs text-muted-foreground">Fetching business metrics, opportunities, and insights</p>
                                             </div>
                                           </div>
                                         </div>
                                       );
                                     }

                                     return (
                                       <ScrollArea className="max-h-[70vh]">
                                         <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 md:p-6">
                                       {/* --- Left Column --- */}
                                       <div className="space-y-3 sm:space-y-4">
                                           {/* Combined Customer & Company Info Card */}
                                           {(() => {
                                             const customerCode = customer.customer_id || customer.id;
                                             const customerDetails = customerDetailsData[customerCode];
                                             
                                             return (
                                           <Card className="bg-background shadow-sm">
                                             <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                               <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                                                 <Building2 className="w-4 h-4" />
                                                 Customer & Company Info
                                               </CardTitle>
                                             </CardHeader>
                                             <CardContent>
                                               <div className="space-y-4">
                                                 {/* Customer Basic Info */}
                                                 <div className="space-y-2">
                                                   <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Customer Details</h4>
                                                   <div className="space-y-1.5 text-sm">
                                                     <div className="flex justify-between">
                                                       <span className="text-muted-foreground">Name:</span>
                                                       <span className="font-medium">{customer.name || "Unknown Customer"}</span>
                                                     </div>
                                                     <div className="flex justify-between">
                                                       <span className="text-muted-foreground">ID:</span>
                                                       <span className="font-medium">{customer.customer_id || customer.id || "N/A"}</span>
                                                     </div>
                                                     <div className="flex justify-between">
                                                       <span className="text-muted-foreground">Address:</span>
                                                       <span className="font-medium text-right flex-1 ml-2">{customer.address || "No address provided"}</span>
                                                     </div>
                                                     {(customer.city || customer.state || customer.zip_code) && (
                                                       <div className="flex justify-between">
                                                         <span className="text-muted-foreground">Location:</span>
                                                         <span className="font-medium">
                                                           {[customer.city, customer.state, customer.zip_code].filter(Boolean).join(", ")}
                                                         </span>
                                                       </div>
                                                     )}
                                                   </div>
                                                 </div>

                                                 {/* Customer Notes */}
                                                 {customer.notes && (
                                                   <div className="space-y-2 pt-2 border-t">
                                                     <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Notes</h4>
                                                     <p className="text-sm text-foreground bg-muted/30 rounded p-2">{customer.notes}</p>
                                                   </div>
                                                 )}

                                                 {/* Company Profile Info */}
                                                 {customerDetails?.company_profile && (
                                                   <div className="space-y-2 pt-2 border-t">
                                                     <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Company Profile</h4>
                                                     <div className="space-y-1.5 text-sm">
                                                       {customerDetails.company_profile.industry && (
                                                         <div className="flex justify-between">
                                                           <span className="text-muted-foreground">Industry:</span>
                                                           <Badge variant="secondary" className="text-xs">{customerDetails.company_profile.industry}</Badge>
                                                         </div>
                                                       )}
                                                       {customerDetails.company_profile.website_url && (
                                                         <div className="flex justify-between">
                                                           <span className="text-muted-foreground">Website:</span>
                                                           <a href={customerDetails.company_profile.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                                                             <Globe className="w-3 h-3" />
                                                             Visit
                                                           </a>
                                                         </div>
                                                       )}
                                                       {customerDetails.company_profile.company_summary && (
                                                         <div className="space-y-1">
                                                           <span className="text-muted-foreground text-xs">Summary:</span>
                                                           <p className="text-xs text-foreground leading-relaxed bg-muted/30 rounded p-2">{customerDetails.company_profile.company_summary}</p>
                                                         </div>
                                                       )}
                                                     </div>
                                                   </div>
                                                 )}

                                               </div>
                                             </CardContent>
                                           </Card>
                                           );
                                         })()}

                                         {/* Discussion Points Card */}
                                         {(() => {
                                           const customerCode = customer.customer_id || customer.id;
                                           const customerDetails = customerDetailsData[customerCode];
                                           
                                           return customerDetails?.discussion_points && customerDetails.discussion_points.length > 0 ? (
                                             <Card className="bg-background shadow-sm">
                                               <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                                 <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                                                   <MessagesSquare className="w-4 h-4" />
                                                   Discussion Points
                                                 </CardTitle>
                                               </CardHeader>
                                               <CardContent>
                                                 <ul className="space-y-2">
                                                   {customerDetails.discussion_points.map((point, i) => (
                                                     <li key={i} className="flex gap-2 items-start">
                                                       <MessageSquare className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                                                       <span className="text-sm">{point}</span>
                                                     </li>
                                                   ))}
                                                 </ul>
                                               </CardContent>
                                             </Card>
                                           ) : null;
                                         })()}

                                         {/* HubSpot Engagement History Card */}
                                         {(isRSM || isBM) && (
                                           <CustomerEngagementHistory customer={customer} />
                                         )}

                                           {/* QORC Analysis Card - enhanced */}
                                           {(() => {
                                             const customerCode = customer.customer_id || customer.id;
                                             const customerDetails = customerDetailsData[customerCode];
                                             
                                             return customerDetails?.qorc_scores ? (
                                             <Card className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/50 dark:to-gray-900/50 shadow-md border-slate-200 dark:border-slate-700">
                                               <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                                                 <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                                   <div className="p-1.5 bg-primary/10 rounded-lg">
                                                     <BarChart3 className="w-4 h-4 text-primary" />
                                                   </div>
                                                   Customer Scorecard
                                                 </CardTitle>
                                                 {customerDetails.qorc_scores.prioritybucket && (
                                                   <Badge 
                                                     variant={
                                                       customerDetails.qorc_scores.prioritybucket === 'High' ? 'default' : 
                                                       customerDetails.qorc_scores.prioritybucket === 'Medium' ? 'secondary' : 'outline'
                                                     }
                                                     className="text-xs font-medium px-3 py-1"
                                                   >
                                                     {customerDetails.qorc_scores.prioritybucket} Priority
                                                   </Badge>
                                                 )}
                                               </CardHeader>
                                               <CardContent>
                                                 <div className="grid grid-cols-2 gap-3 mb-4">
                                                   <div className="bg-muted/30 border rounded-lg p-3 text-center">
                                                     <p className="text-2xl font-bold text-foreground">{customerDetails.qorc_scores.qscore || 'N/A'}</p>
                                                     <p className="text-xs text-muted-foreground font-medium">Q-Score</p>
                                                   </div>
                                                   <div className="bg-muted/30 border rounded-lg p-3 text-center">
                                                     <p className="text-2xl font-bold text-foreground">{customerDetails.qorc_scores.oscore || 'N/A'}</p>
                                                     <p className="text-xs text-muted-foreground font-medium">O-Score</p>
                                                   </div>
                                                   <div className="bg-muted/30 border rounded-lg p-3 text-center">
                                                     <p className="text-2xl font-bold text-foreground">{customerDetails.qorc_scores.rscore || 'N/A'}</p>
                                                     <p className="text-xs text-muted-foreground font-medium">R-Score</p>
                                                   </div>
                                                   <div className="bg-muted/30 border rounded-lg p-3 text-center">
                                                     <p className="text-2xl font-bold text-foreground">{customerDetails.qorc_scores.cscore || 'N/A'}</p>
                                                     <p className="text-xs text-muted-foreground font-medium">C-Score</p>
                                                   </div>
                                                 </div>
                                                 <div className="bg-muted/20 rounded-lg p-3 border">
                                                   <div className="flex justify-between items-center mb-2">
                                                     <span className="text-sm text-muted-foreground font-medium">Priority Score:</span>
                                                     <span className="text-lg font-bold text-foreground">{customerDetails.qorc_scores.priorityscore || 'N/A'}</span>
                                                   </div>
                                                   <div className="flex justify-between items-center">
                                                     <span className="text-sm text-muted-foreground font-medium">Ranking:</span>
                                                     <Badge variant="outline" className="text-xs font-semibold">
                                                       #{customerDetails.qorc_scores.priorityranking || 'N/A'}
                                                     </Badge>
                                                   </div>
                                                 </div>
                                               </CardContent>
                                             </Card>
                                           ) : null;
                                         })()}

                                       </div>

                                       {/* --- Right Column --- */}
                                       <div className="space-y-3 sm:space-y-4">

                                         {/* Customer Details Loading/Error State */}
                                         {(() => {
                                           const customerCode = customer.customer_id || customer.id;
                                           const isLoading = customerDetailsLoading[customerCode];
                                           const customerDetails = customerDetailsData[customerCode];

                                           if (isLoading) {
                                             return (
                                               <Card className="bg-background shadow-sm">
                                                 <CardContent className="py-6">
                                                   <div className="flex items-center justify-center">
                                                     <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                                                     <span className="ml-2 text-sm text-muted-foreground">Loading customer details...</span>
                                                   </div>
                                                 </CardContent>
                                               </Card>
                                             );
                                           }

                                           if (!customerDetails) {
                                             return (
                                               <Card className="bg-background shadow-sm">
                                                 <CardContent className="py-6 text-center">
                                                   <p className="text-sm text-muted-foreground">Customer details not available</p>
                                                 </CardContent>
                                               </Card>
                                             );
                                           }

                                           return (
                                             <>
                                               {/* Business Metrics Card */}
                                               {customerDetails?.business_metrics && (
                                                 <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 shadow-md border-green-200 dark:border-green-700">
                                                   <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                                                     <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                                       <div className="p-1.5 bg-green-100 dark:bg-green-900 rounded-lg">
                                                         <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                                                       </div>
                                                       Business Performance
                                                     </CardTitle>
                                                   </CardHeader>
                                                   <CardContent>
                                                     <div className="grid grid-cols-2 gap-3">
                                                       <div className="bg-muted/30 border rounded-lg p-3">
                                                         <p className="text-lg font-bold text-foreground">
                                                           {customerDetails.business_metrics.order_count || 0}
                                                         </p>
                                                         <p className="text-xs text-muted-foreground font-medium">Total Orders</p>
                                                         <p className="text-sm font-semibold text-foreground mt-1">
                                                           ${(customerDetails.business_metrics.order_value || 0).toLocaleString()}
                                                         </p>
                                                       </div>
                                                       <div className="bg-muted/30 border rounded-lg p-3">
                                                         <p className="text-lg font-bold text-foreground">
                                                           {customerDetails.business_metrics.quote_count || 0}
                                                         </p>
                                                         <p className="text-xs text-muted-foreground font-medium">Total Quotes</p>
                                                         <p className="text-sm font-semibold text-foreground mt-1">
                                                           ${(customerDetails.business_metrics.quote_value || 0).toLocaleString()}
                                                         </p>
                                                       </div>
                                                       <div className="bg-muted/30 border rounded-lg p-3 text-center">
                                                         <p className="text-lg font-bold text-foreground">
                                                           {customerDetails.business_metrics.rental_count || 0}
                                                         </p>
                                                         <p className="text-xs text-muted-foreground font-medium">Rentals</p>
                                                       </div>
                                                       <div className="bg-muted/30 border rounded-lg p-3 text-center">
                                                         <p className="text-lg font-bold text-foreground">
                                                           {customerDetails.business_metrics.churn_rank || 'N/A'}
                                                         </p>
                                                         <p className="text-xs text-muted-foreground font-medium">Churn Risk</p>
                                                       </div>
                                                     </div>
                                                   </CardContent>
                                                 </Card>
                                               )}

                                               {/* Active Opportunities Card */}
                                               {(() => {
                                                 const customerCode = customer.customer_id || customer.id;
                                                 const customerOpportunities = customerOpportunitiesData[customerCode];
                                                 
                                                 // Calculate counts from opportunities response
                                                 let quoteCount = 0;
                                                 let orderCount = 0;
                                                 let rentalCount = 0;
                                                 
                                                 if (customerOpportunities?.data) {
                                                   customerOpportunities.data.forEach(opp => {
                                                     if (opp.type === 'quote') quoteCount++;
                                                     else if (opp.type === 'order') orderCount++;
                                                     else if (opp.type === 'rental') rentalCount++;
                                                   });
                                                 }
                                                 
                                                 return (
                                               <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/30 dark:to-blue-900/30 shadow-md border-indigo-200 dark:border-indigo-700">
                                                 <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                                                   <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                                     <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                                                       <Target className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                                     </div>
                                                     Active Quotes, Orders and Rentals
                                                   </CardTitle>
                                                   {/* View All Button */}
                                                   {(quoteCount > 0 || orderCount > 0 || rentalCount > 0) && (
                                                     <Button 
                                                       variant="outline" 
                                                       size="sm" 
                                                       className="text-xs h-6"
                                                       onClick={() => {
                                                         // Format opportunities for the dialog  
                                                         const allOpportunities = customerOpportunities?.data || [];
                                                         const quotes = allOpportunities.filter(opp => opp.type === 'quote');
                                                         const orders = allOpportunities.filter(opp => opp.type === 'order');
                                                         const rentals = allOpportunities.filter(opp => opp.type === 'rental');
                                                         
                                                         setSelectedCustomerOpportunities({
                                                           customer: customer,
                                                           details: {
                                                             ...customerDetails,
                                                             open_quotes: quotes,
                                                             open_orders: orders,
                                                             active_rentals: rentals
                                                           },
                                                           opportunities: customerOpportunities?.data || []
                                                         });
                                                         setOpportunitiesDialogOpen(true);
                                                       }}
                                                     >
                                                       View All
                                                     </Button>
                                                   )}
                                                 </CardHeader>
                                                 <CardContent>
                                                   {/* Summary counts */}
                                                   <div className="grid grid-cols-3 gap-3 text-center mb-4">
                                                     <div className="bg-muted/30 border rounded-lg p-3">
                                                       <p className="text-xl font-bold text-foreground">{quoteCount}</p>
                                                       <p className="text-xs text-muted-foreground font-medium">Quotes</p>
                                                     </div>
                                                     <div className="bg-muted/30 border rounded-lg p-3">
                                                       <p className="text-xl font-bold text-foreground">{orderCount}</p>
                                                       <p className="text-xs text-muted-foreground font-medium">Orders</p>
                                                     </div>
                                                     <div className="bg-muted/30 border rounded-lg p-3">
                                                       <p className="text-xl font-bold text-foreground">{rentalCount}</p>
                                                       <p className="text-xs text-muted-foreground font-medium">Rentals</p>
                                                     </div>
                                                   </div>

                                                   {/* Details - only show if there are opportunities */}
                                                   {(quoteCount > 0 || orderCount > 0 || rentalCount > 0) && customerOpportunities?.data && (
                                                     <div className="space-y-3 max-h-48 overflow-y-auto">
                                                       {/* Show opportunities grouped by type */}
                                                       {customerOpportunities.data
                                                         .filter(opp => opp.type === 'quote')
                                                         .slice(0, 3)
                                                         .map((quote, idx) => (
                                                         <div key={idx} className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/30 rounded p-2">
                                                           <div className="flex items-center justify-between mb-1">
                                                             <div className="flex items-center gap-1">
                                                               <FileText className="w-3 h-3 text-blue-600" />
                                                               <span className="text-xs font-semibold">Quote</span>
                                                             </div>
                                                             <span className="text-xs font-bold text-blue-600">
                                                               ${(quote.estimatedvalue || 0).toLocaleString()}
                                                             </span>
                                                           </div>
                                                           <p className="text-xs font-medium truncate">{quote.opportunityname}</p>
                                                           <p className="text-xs text-muted-foreground">
                                                             #{quote.opportunitynum} {quote.markettype ? `• ${quote.markettype}` : ''}
                                                           </p>
                                                         </div>
                                                       ))}
                                                       {quoteCount > 3 && (
                                                         <p className="text-xs text-muted-foreground text-center">
                                                           +{quoteCount - 3} more quotes
                                                         </p>
                                                       )}
                                                       
                                                       {/* Orders */}
                                                       {customerOpportunities.data
                                                         .filter(opp => opp.type === 'order')
                                                         .slice(0, 2)
                                                         .map((order, idx) => (
                                                         <div key={idx} className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800/30 rounded p-2">
                                                           <div className="flex items-center justify-between mb-1">
                                                             <div className="flex items-center gap-1">
                                                               <CheckCircle2 className="w-3 h-3 text-green-600" />
                                                               <span className="text-xs font-semibold">Order</span>
                                                             </div>
                                                             <span className="text-xs font-bold text-green-600">
                                                               ${(order.estimatedvalue || 0).toLocaleString()}
                                                             </span>
                                                           </div>
                                                           <p className="text-xs font-medium truncate">{order.opportunityname}</p>
                                                           <p className="text-xs text-muted-foreground">
                                                             #{order.opportunitynum} {order.markettype ? `• ${order.markettype}` : ''}
                                                           </p>
                                                         </div>
                                                       ))}
                                                       
                                                       {/* Rentals */}
                                                       {customerOpportunities.data
                                                         .filter(opp => opp.type === 'rental')
                                                         .slice(0, 2)
                                                         .map((rental, idx) => (
                                                         <div key={idx} className="bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800/30 rounded p-2">
                                                           <div className="flex items-center justify-between mb-1">
                                                             <div className="flex items-center gap-1">
                                                               <Package className="w-3 h-3 text-purple-600" />
                                                               <span className="text-xs font-semibold">Rental</span>
                                                             </div>
                                                             <span className="text-xs font-bold text-purple-600">
                                                               ${(rental.estimatedvalue || 0).toLocaleString()}
                                                             </span>
                                                           </div>
                                                           <p className="text-xs font-medium truncate">{rental.opportunityname}</p>
                                                           <p className="text-xs text-muted-foreground">
                                                             #{rental.opportunitynum}
                                                           </p>
                                                         </div>
                                                       ))}
                                                     </div>
                                                   )}
                                                 </CardContent>
                                               </Card>
                                               );
                                             })()}

                                               {/* Action Items Card */}
                                               {customerDetails?.action_items && customerDetails.action_items.length > 0 && (
                                                 <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 shadow-md border-amber-200 dark:border-amber-700">
                                                   <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                                                     <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                                       <div className="p-1.5 bg-amber-100 dark:bg-amber-900 rounded-lg">
                                                         <ListChecks className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                                       </div>
                                                       Action Items ({customerDetails.action_items.length})
                                                     </CardTitle>
                                                   </CardHeader>
                                                   <CardContent>
                                                     <div className="space-y-2 max-h-64 overflow-y-auto">
                                                       {customerDetails.action_items.map((action, i) => (
                                                         <div key={i} className="group flex items-start gap-3 bg-muted/20 hover:bg-muted/30 border rounded-lg p-3 transition-colors">
                                                           <CheckSquare className="w-4 h-4 mt-0.5 text-muted-foreground group-hover:text-foreground flex-shrink-0 transition-colors" />
                                                           <div className="flex-1">
                                                             <p className="text-sm text-foreground leading-relaxed">{action}</p>
                                                             <div className="flex items-center gap-2 mt-2">
                                                               <Badge variant="outline" className="text-xs">
                                                                 Item #{i + 1}
                                                               </Badge>
                                                               <span className="text-xs text-muted-foreground">Priority: Normal</span>
                                                             </div>
                                                           </div>
                                                         </div>
                                                       ))}
                                                       {customerDetails.action_items.length === 0 && (
                                                         <div className="text-center py-4 text-muted-foreground">
                                                           <ClipboardCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                                           <p className="text-sm">No action items found</p>
                                                         </div>
                                                       )}
                                                     </div>
                                                   </CardContent>
                                                 </Card>
                                               )}

                                               {/* Churn Metrics Card */}
                                               <ChurnMetricsCard 
                                                 customerCode={customer.customer_id || customer.id}
                                                 churnData={getChurnDataForCustomer(customer.customer_id || customer.customer_code || customer.code || customer.id)}
                                                 isLoading={churnLoading}
                                               />
                                             </>
                                           );
                                         })()}

                                         {/* PrioritizedDataCard - if it shows different data than what we have above */}
                                         <PrioritizedDataCard
                                           customer={customer}
                                         />
                                       </div>
                                     </div>
                                   </ScrollArea>
                                       );
                                   })()}
                                   {/* End of main dialog ScrollArea */}
                                   {/* Add update status button for RSMs at the bottom of the dialog content */}
                                   {isRSM && (
                                     <div className="p-4 border-t grid grid-cols-2 gap-2">
                                       <Button
                                         onClick={() =>
                                           handleOpenProgressDialog(customer)
                                         }
                                       >
                                         Update Status
                                       </Button>
                                       <Button
                                         onClick={() =>
                                           handleOpenHubspotDialog(customer)
                                         }
                                         variant="outline"
                                       >
                                         <Projector className="mr-2 h-4 w-4" />
                                         Update HubSpot
                                       </Button>
                                     </div>
                                   )}
                                   {/* Add BM notes button in the customer dialog for BMs */}
                                   {isBM && (
                                     <div className="p-4 border-t">
                                       <Button
                                         onClick={() =>
                                           handleOpenBmNotesDialog(customer)
                                         }
                                         className="w-full"
                     variant="outline"
                                       >
                                         {customer.bm_notes
                                           ? "Edit Notes"
                                           : "Add Notes"}
                                       </Button>
                                     </div>
                                   )}
                                   {/* Show BM notes if they exist and user is RSM or BM */}
                                   {customer.bm_notes && (
                                     <div className="px-4 pb-4">
                                       <div className="bg-muted/40 border rounded-md p-3">
                                         <div className="font-medium text-sm mb-1">
                                           Business Manager Notes:
                                         </div>
                                         <div className="text-sm whitespace-pre-wrap">
                                           {customer.bm_notes.notes}
                                         </div>
                                         <div className="text-xs text-muted-foreground mt-1">
                                           Updated:{" "}
                                           {formatDate(
                                             customer.bm_notes.updated_at,
                                           )}
                                         </div>
                                       </div>
                                     </div>
                                   )}
                                 </DialogContent>
                              </Dialog>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No customer stops found for this itinerary</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Tab Content (Notes) */}
                <TabsContent value="notes" className="space-y-4 mt-0">
                  <Card>
                    <CardHeader>
                      <CardTitle>Schedule Notes</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* BM Notes Card */}
                      <div>
                        <h3 className="text-base font-medium mb-2">
                          Business Manager Notes
                        </h3>
                        <Card className="bg-background shadow-sm">
                          <CardHeader className="p-3 pb-2">
                            <div className="flex justify-between items-center">
                              <CardTitle className="text-sm font-medium">
                                Business Manager Notes
                              </CardTitle>
                              {isBM && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex items-center gap-1 h-7"
                                  onClick={() => handleOpenBmNotesDialog(null)} // Pass null for schedule-level notes
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                  {clusterData?.bm_notes ? "Edit" : "Add"}
                                </Button>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="p-3 pt-0">
                            {clusterData?.bm_notes ? (
                              <div>
                                <div className="text-sm whitespace-pre-wrap border-l-2 border-primary/20 pl-3 py-1">
                                  {clusterData.bm_notes.notes}
                                </div>
                                <div className="text-xs text-muted-foreground mt-2">
                                  Updated:{" "}
                                  {formatDate(clusterData.bm_notes.updated_at)}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground italic">
                                No business manager notes for this schedule.
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      </div>

                      {/* RSM Notes Card */}
                      <div>
                        <h3 className="text-base font-medium mb-2">
                          RSM Notes
                        </h3>
                        <Card className="bg-background shadow-sm">
                          <CardHeader className="p-3 pb-2">
                            <div className="flex justify-between items-center">
                              <CardTitle className="text-sm font-medium">
                                RSM Notes
                              </CardTitle>
                              {isRSM && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex items-center gap-1 h-7"
                                  onClick={handleOpenRsmNotesDialog}
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                  {clusterData?.rsm_notes ? "Edit" : "Add"}
                                </Button>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="p-3 pt-0">
                            {clusterData?.rsm_notes ? (
                              <div>
                                <div className="text-sm whitespace-pre-wrap border-l-2 border-secondary/30 pl-3 py-1">
                                  {clusterData.rsm_notes.notes}
                                </div>
                                <div className="text-xs text-muted-foreground mt-2">
                                  Updated:{" "}
                                  {formatDate(clusterData.rsm_notes.updated_at)}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground italic">
                                No RSM notes for this schedule.
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      </div>

                      {/* Customer Notes Section */}
                      <div>
                        <h3 className="text-base font-medium mb-2">
                          Customer Notes
                        </h3>
                        {customers.length > 0 ? (
                          <div className="space-y-3">
                            {customers.map((customer, idx) => (
                              <Card
                                key={customer.id || idx}
                                className="bg-background shadow-sm"
                              >
                                <CardHeader className="p-3 pb-2">
                                  <div className="flex justify-between items-center">
                                    <CardTitle className="text-sm font-medium">
                                      {customer.name}
                                    </CardTitle>
                                    {isBM && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="flex items-center gap-1 h-7"
                                        onClick={() =>
                                          handleOpenBmNotesDialog(customer)
                                        }
                                      >
                                        <Edit className="h-3.5 w-3.5" />
                                        {customer.bm_notes ? "Edit" : "Add"}
                                      </Button>
                                    )}
                                  </div>
                                </CardHeader>
                                <CardContent className="p-3 pt-0">
                                  {customer.bm_notes ? (
                                    <div>
                                      <div className="text-sm whitespace-pre-wrap border-l-2 border-primary/20 pl-3 py-1">
                                        {customer.bm_notes.notes}
                                      </div>
                                      <div className="text-xs text-muted-foreground mt-2">
                                        Updated:{" "}
                                        {formatDate(
                                          customer.bm_notes.updated_at,
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground italic">
                                      No notes for this customer.
                                    </p>
                                  )}
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        ) : (
                          <Card className="bg-background shadow-sm">
                            <CardContent className="p-4 text-center">
                              <p className="text-sm text-muted-foreground italic">
                                No customers in this itinerary.
                              </p>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* New Messages tab content */}
                <TabsContent
                  value="messages"
                  className="mt-0 h-[70vh] sm:h-[600px]"
                >
                  <MessageCenter
                    scheduleId={clusterData?.cluster}
                    rsmId={clusterData?.assigned_to?.id}
                    rsmName={clusterData?.assigned_to?.full_name}
                  />
                </TabsContent>
              </div>

              {/* Overall Itinerary Feedback - Move inside scrollable area on mobile */}
              <div
                className={`${isMobile ? "mt-4 mb-20" : "mt-8 border-t pt-6"} flex-shrink-0 px-1 pb-4`}
              >
                {/* Overall Itinerary Feedback */}
                <Card className="shadow-sm">
                  <CardContent
                    className={`p-3 ${isMobile ? "pt-3 pb-2" : "p-4"}`}
                  >
                    <FeedbackComponent
                      question="Overall, how helpful is this generated itinerary?"
                      feedback={itineraryFeedback}
                      setFeedback={setItineraryFeedback}
                      feedbackKey="itinerary"
                      context={feedbackContext}
                      showRatings={true}
                      ratingLabels={[
                        "Grouping",
                        "Efficiency",
                        "Coverage",
                        "Overall",
                      ]}
                      isMobile={isMobile}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Mobile Bottom Navigation */}
              {isMobile && !isMapFullScreen && <MobileBottomNav />}
            </Tabs>
          </div>
        )}

        {/* Reasoning Dialog */}
        {reasoning && (
          <Dialog
            open={showReasoningDialog}
            onOpenChange={setShowReasoningDialog}
          >
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Generation Reasoning</DialogTitle>
              </DialogHeader>
              <div className="prose prose-sm dark:prose-invert max-w-none bg-muted/30 p-4 rounded-md border">
                <div
                  dangerouslySetInnerHTML={{
                    __html: marked.parse(reasoning || ""),
                  }}
                />
              </div>
              <div className="mt-6">
                {/* Reasoning Feedback */}
                <FeedbackComponent
                  question="Was this reasoning helpful?"
                  feedback={reasoningFeedback}
                  setFeedback={setReasoningFeedback}
                  feedbackKey="reasoning"
                  context={feedbackContext}
                  isMobile={isMobile}
                />
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* BM Notes Dialog */}
        {isBM && (
          <Dialog open={bmNotesOpen} onOpenChange={setBmNotesOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {currentCustomerForNotes
                    ? `Business Manager Notes for ${currentCustomerForNotes.name}`
                    : "Business Manager Notes for Schedule"}
                </DialogTitle>
                <DialogDescription>
                  {currentCustomerForNotes
                    ? "Add or update your notes for this customer stop"
                    : "Add or update your notes for this schedule"}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Notes</label>
                  <Textarea
                    value={bmNotes}
                    onChange={(e) => setBmNotes(e.target.value)}
                    placeholder={
                      currentCustomerForNotes
                        ? "Add your notes about this customer stop"
                        : "Add your notes about this schedule"
                    }
                    rows={6}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setBmNotesOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateBmNotes}>Save Notes</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* RSM Notes Dialog */}
        {isRSM && (
          <Dialog open={rsmNotesOpen} onOpenChange={setRsmNotesOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>RSM Notes for Schedule</DialogTitle>
                <DialogDescription>
                  Add or update your notes for this schedule
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Notes</label>
                  <Textarea
                    value={rsmNotes}
                    onChange={(e) => setRsmNotes(e.target.value)}
                    placeholder="Add your notes about this schedule"
                    rows={6}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setRsmNotesOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleUpdateRsmNotes}>Save Notes</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Assign RSM Modal - placed in detailed view */}
        {isAssignModalOpen && (
          <AssignRsmModal
            isOpen={isAssignModalOpen}
            onClose={() => {
              console.log("[SavedByoiPage] Closing AssignRsmModal");
              setIsAssignModalOpen(false);
            }}
            clusterId={selectedItinerary.itinerary_id}
            clusterName={selectedItinerary.name}
            onAssignComplete={handleAssignComplete}
          />
        )}

        {/* Assign RSM Modal - placed in detailed view */}
        {isAssignModalOpen && (
          <AssignRsmModal
            isOpen={isAssignModalOpen}
            onClose={() => {
              console.log("[SavedByoiPage] Closing AssignRsmModal");
              setIsAssignModalOpen(false);
            }}
            clusterId={selectedItinerary.itinerary_id}
            clusterName={selectedItinerary.name}
            onAssignComplete={handleAssignComplete}
          />
        )}
      </div>
    );
  }

  // Main list view - unchanged from original
  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Header />

      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="mb-4"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>

            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold">Saved Itineraries</h1>
                <p className="text-muted-foreground mt-1">
                  View and manage your custom built itineraries
                </p>
              </div>

              <Button onClick={() => navigate("/build-itinerary")}>
                Create New Itinerary
              </Button>
            </div>

            {/* Search Bar */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search itineraries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : filteredItineraries.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <CalendarIcon className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">
                  No itineraries found
                </h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery
                    ? "Try adjusting your search"
                    : "Create your first custom itinerary"}
                </p>
                {!searchQuery && (
                  <Button onClick={() => navigate("/build-itinerary")}>
                    Build Your Own Itinerary
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredItineraries.map((itinerary) => (
                <Card
                  key={itinerary.itinerary_id}
                  className="hover:shadow-lg transition-shadow"
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg">
                          {itinerary.name}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {itinerary.description}
                        </p>
                      </div>
                      <Badge
                        variant={
                          itinerary.status === "active"
                            ? "default"
                            : "secondary"
                        }
                      >
                        {itinerary.status}
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                        <span>
                          Week of{" "}
                          {format(new Date(itinerary.week_of), "MMM dd, yyyy")}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{itinerary.total_customers} customers</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>
                          {Math.floor(itinerary.estimated_duration / 60)}h{" "}
                          {itinerary.estimated_duration % 60}m
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() =>
                          handleViewDetails(itinerary.itinerary_id)
                        }
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() =>
                          navigate(
                            `/build-itinerary?edit=${itinerary.itinerary_id}`,
                          )
                        }
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteConfirm(itinerary)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirm}
        onOpenChange={() => setDeleteConfirm(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Itinerary</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(deleteConfirm.itinerary_id)}
              variant="destructive"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comprehensive Opportunities Dialog */}
      <Dialog
        open={opportunitiesDialogOpen && selectedCustomerOpportunities}
        onOpenChange={(open) => {
          if (!open) {
            setOpportunitiesDialogOpen(false);
            setOpportunitiesFilter('all');
            setOpportunitiesSort('amount_desc');
            setOpportunitiesSearch('');
          }
        }}
      >
        <DialogContent className="max-w-6xl w-[95vw] h-[85vh] p-0">
          <DialogHeader className="p-4 border-b bg-muted/30">
            <DialogTitle className="text-xl font-semibold">
              {selectedCustomerOpportunities?.customer?.name || "Customer"} - All Opportunities
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              View all quotes, orders, and rentals for this customer. Use filters to find specific opportunities.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 flex flex-col min-h-0">
            {/* Filter Controls */}
            <div className="border-b bg-muted/20 p-4 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Type Filter */}
                <div className="flex-1">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Filter by Type
                  </label>
                  <select
                    value={opportunitiesFilter}
                    onChange={(e) => setOpportunitiesFilter(e.target.value)}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="all">All Types</option>
                    <option value="quotes">Quotes Only</option>
                    <option value="orders">Orders Only</option>
                    <option value="rentals">Rentals Only</option>
                  </select>
                </div>

                {/* Sort */}
                <div className="flex-1">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Sort by
                  </label>
                  <select
                    value={opportunitiesSort}
                    onChange={(e) => setOpportunitiesSort(e.target.value)}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="amount_desc">Amount (High to Low)</option>
                    <option value="amount_asc">Amount (Low to High)</option>
                    <option value="name_asc">Name (A to Z)</option>
                    <option value="date_desc">Date (Newest First)</option>
                  </select>
                </div>

                {/* Search */}
                <div className="flex-1">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Search
                  </label>
                  <input
                    type="text"
                    placeholder="Search opportunities..."
                    value={opportunitiesSearch}
                    onChange={(e) => setOpportunitiesSearch(e.target.value)}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  />
                </div>
              </div>

              {/* Map Filter Toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="mappable-filter"
                  checked={showOnlyMappable}
                  onChange={(e) => setShowOnlyMappable(e.target.checked)}
                  className="rounded border-input"
                />
                <label htmlFor="mappable-filter" className="text-sm font-medium">
                  Show only opportunities with locations
                </label>
              </div>
            </div>

            {/* Opportunities List */}
            <ScrollArea className="flex-1">
              <div className="p-4">
                {(() => {
                  // Get opportunities and apply filters
                  const opportunities = selectedCustomerOpportunities?.opportunities || [];
                  
                  // Filter by type
                  let filteredOpportunities = opportunities;
                  if (opportunitiesFilter !== 'all') {
                    filteredOpportunities = opportunities.filter(opp => opp.type === opportunitiesFilter.replace('s', ''));
                  }

                  // Filter by mappable
                  if (showOnlyMappable) {
                    filteredOpportunities = filteredOpportunities.filter(opp => 
                      opp.coordinates && opp.coordinates.lat && opp.coordinates.lng
                    );
                  }

                  // Filter by search
                  if (opportunitiesSearch) {
                    const searchLower = opportunitiesSearch.toLowerCase();
                    filteredOpportunities = filteredOpportunities.filter(opp =>
                      (opp.opportunityname || '').toLowerCase().includes(searchLower) ||
                      (opp.opportunitynum || '').toLowerCase().includes(searchLower) ||
                      (opp.ordernumber || '').toLowerCase().includes(searchLower)
                    );
                  }

                  // Sort opportunities
                  filteredOpportunities.sort((a, b) => {
                    switch (opportunitiesSort) {
                      case 'amount_desc':
                        return (b.estimatedvalue || 0) - (a.estimatedvalue || 0);
                      case 'amount_asc':
                        return (a.estimatedvalue || 0) - (b.estimatedvalue || 0);
                      case 'name_asc':
                        return (a.opportunityname || '').localeCompare(b.opportunityname || '');
                      case 'date_desc':
                        return new Date(b.lastupdate || 0) - new Date(a.lastupdate || 0);
                      default:
                        return 0;
                    }
                  });

                  if (filteredOpportunities.length === 0) {
                    return (
                      <div className="text-center py-12 text-muted-foreground">
                        <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">No opportunities found</p>
                        <p className="text-sm">Try adjusting your filters or search terms</p>
                      </div>
                    );
                  }

                  return (
                    <div className="grid gap-3">
                      {filteredOpportunities.map((opportunity, idx) => (
                        <Card key={idx} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  {opportunity.type === 'quote' && <FileText className="h-4 w-4 text-blue-600" />}
                                  {opportunity.type === 'order' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                                  {opportunity.type === 'rental' && <Heart className="h-4 w-4 text-purple-600" />}
                                  <Badge variant="outline" className="text-xs capitalize">
                                    {opportunity.type}
                                  </Badge>
                                  {opportunity.markettype && (
                                    <Badge variant="secondary" className="text-xs">
                                      {opportunity.markettype}
                                    </Badge>
                                  )}
                                </div>
                                
                                <h4 className="font-medium text-sm truncate mb-1">
                                  {opportunity.opportunityname || opportunity.opportunitynum || "Unnamed Opportunity"}
                                </h4>
                                
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                  <span>#{opportunity.opportunitynum}</span>
                                  {opportunity.lastupdate && (
                                    <span>{formatDate(opportunity.lastupdate)}</span>
                                  )}
                                </div>

                                {/* Address Information */}
                                {(opportunity.address || opportunity.city || opportunity.projectcity) && (
                                  <div className="mt-3 pt-3 border-t">
                                    <p className="text-xs font-medium text-muted-foreground mb-2">
                                      Location
                                    </p>
                                    <div className="flex items-start gap-2">
                                      <MapPin className="w-3 h-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                                      <div className="flex-1">
                                        {opportunity.address && (
                                          <p className="text-xs text-foreground mb-1">
                                            {opportunity.address}
                                          </p>
                                        )}
                                        <p className="text-xs text-muted-foreground">
                                          {[
                                            opportunity.city || opportunity.projectcity,
                                            opportunity.state || opportunity.projectstate,
                                            opportunity.zipcode
                                          ].filter(Boolean).join(', ')}
                                        </p>
                                        {opportunity.markettype && (
                                          <p className="text-xs text-muted-foreground mt-1">
                                            Market: {opportunity.markettype}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              <div className="text-right">
                                <div className="text-lg font-bold">
                                  ${(opportunity.estimatedvalue || 0).toLocaleString()}
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                  {opportunity.coordinates && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-xs"
                                      onClick={() => {
                                        setSelectedMapOpportunity(opportunity);
                                        setMapPopupOpen(true);
                                      }}
                                    >
                                      <Map className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Map Popup Dialog */}
      <Dialog open={mapPopupOpen} onOpenChange={(open) => {
        setMapPopupOpen(open);
        if (!open) {
          // Small delay to allow map cleanup
          setTimeout(() => {
            setSelectedMapOpportunity(null);
          }, 100);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {selectedMapOpportunity?.opportunityname || selectedMapOpportunity?.opportunitynum || "Opportunity"} - Location Map
            </DialogTitle>
          </DialogHeader>
          
          {selectedMapOpportunity && mapPopupOpen && (
            <div className="space-y-4">
              {/* Opportunity Info Bar */}
              <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                <Badge variant="outline" className={`text-xs font-medium ${
                  selectedMapOpportunity.type === 'quote' ? 'border-blue-500 text-blue-700' :
                  selectedMapOpportunity.type === 'order' ? 'border-green-500 text-green-700' :
                  'border-purple-500 text-purple-700'
                }`}>
                  {selectedMapOpportunity.type?.toUpperCase()}
                </Badge>
                <div className="flex-1">
                  <p className="font-medium">{selectedMapOpportunity.opportunityname || selectedMapOpportunity.opportunitynum}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedMapOpportunity.address ? selectedMapOpportunity.address + ', ' : ''}
                    {[
                      selectedMapOpportunity.city || selectedMapOpportunity.projectcity,
                      selectedMapOpportunity.state || selectedMapOpportunity.projectstate,
                      selectedMapOpportunity.zipcode
                    ].filter(Boolean).join(', ')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold">${(selectedMapOpportunity.estimatedvalue || 0).toLocaleString()}</p>
                  {selectedMapOpportunity.coordinates && (
                    <p className="text-xs text-muted-foreground font-mono">
                      {selectedMapOpportunity.coordinates.lat?.toFixed(4)}, {selectedMapOpportunity.coordinates.lng?.toFixed(4)}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Map Container */}
              <div className="h-96 rounded-lg overflow-hidden border">
                {(selectedMapOpportunity.coordinates?.lat && selectedMapOpportunity.coordinates?.lng) ? (
                  <MapContainer
                    key={`map-${selectedMapOpportunity.opportunitynum}-${selectedMapOpportunity.type}`}
                    center={[selectedMapOpportunity.coordinates.lat, selectedMapOpportunity.coordinates.lng]}
                    zoom={15}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <Marker 
                      position={[selectedMapOpportunity.coordinates.lat, selectedMapOpportunity.coordinates.lng]}
                      icon={L.divIcon({
                        className: 'custom-div-icon',
                        html: `<div style="background-color: ${
                          selectedMapOpportunity.type === 'quote' ? '#3b82f6' :
                          selectedMapOpportunity.type === 'order' ? '#10b981' : '#8b5cf6'
                        }; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
                        iconSize: [20, 20],
                        iconAnchor: [10, 10]
                      })}
                    >
                      <LeafletPopup>
                        <div className="p-2">
                          <h3 className="font-semibold">{selectedMapOpportunity.opportunityname || selectedMapOpportunity.opportunitynum}</h3>
                          <p className="text-sm text-muted-foreground">#{selectedMapOpportunity.opportunitynum}</p>
                          <p className="text-sm">
                            {selectedMapOpportunity.address ? selectedMapOpportunity.address + ', ' : ''}
                            {[
                              selectedMapOpportunity.city || selectedMapOpportunity.projectcity,
                              selectedMapOpportunity.state || selectedMapOpportunity.projectstate
                            ].filter(Boolean).join(', ')}
                          </p>
                          <p className="font-medium">${(selectedMapOpportunity.estimatedvalue || 0).toLocaleString()}</p>
                        </div>
                      </LeafletPopup>
                    </Marker>
                  </MapContainer>
                ) : (
                  <div className="flex items-center justify-center h-full bg-muted/20">
                    <p className="text-muted-foreground">No location coordinates available</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
